(function () {
    'use strict';

    document.documentElement.classList.add('wh1-js');

    let installPrompt = null;

    function isIos() {
        return /iPad|iPhone|iPod/.test(navigator.userAgent)
            || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    }

    function installElements() {
        return {
            button: document.querySelector('[data-kussin-pwa-install]'),
            modal: document.querySelector('[data-kussin-pwa-modal]'),
        };
    }

    function showIosInstructions(modal) {
        if (!modal || !window.bootstrap || !window.bootstrap.Modal) {
            return;
        }

        window.bootstrap.Modal.getOrCreateInstance(modal).show();
    }

    async function requestInstall() {
        const { modal } = installElements();

        if (isIos()) {
            showIosInstructions(modal);
            return;
        }

        if (!installPrompt) {
            return;
        }

        const prompt = installPrompt;
        installPrompt = null;
        await prompt.prompt();
        await prompt.userChoice;
    }

    function initInstallControl() {
        const { button } = installElements();

        if (!button || button.dataset.kussinPwaBound === '1') {
            return;
        }

        button.dataset.kussinPwaBound = '1';
        button.addEventListener('click', requestInstall);

    }

    function registerServiceWorker() {
        const manifest = document.querySelector('link[rel="manifest"][href="/manifest.webmanifest"]');

        if (!manifest || !('serviceWorker' in navigator)) {
            return;
        }

        navigator.serviceWorker.register('/service-worker.js', { scope: '/' }).catch((error) => {
            console.error('Warehouse One service worker registration failed.', error);
        });
    }

    function initFactFinderHeaderSearch() {
        if (document.documentElement.dataset.kussinFactFinderHeaderFocusBound === '1') {
            return;
        }

        document.documentElement.dataset.kussinFactFinderHeaderFocusBound = '1';

        // #70425 — the overlay used to open on any focus inside the header search form. Reached by
        // Tab, that threw every keyboard user into the overlay and, with the page scroll locked
        // behind it, nothing past the search could be reached. A pointer still opens the overlay
        // through the click the searchbox script listens for; the keyboard opens it on intent:
        // typing a character, Enter or the down arrow in the empty header field.
        const triggerOf = (target) => (target && target.closest
            ? target.closest('.kussin-factfinder-search-overlay-trigger')
            : null);
        const overlayEl = () => document.querySelector('.kussin-factfinder-search-overlay-wrapper');
        const openFromTrigger = (trigger) => trigger.dispatchEvent(new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
        }));

        document.addEventListener('keydown', (event) => {
            const trigger = triggerOf(event.target);
            const overlay = overlayEl();

            if (!trigger || !overlay || overlay.contains(trigger) || event.altKey || event.ctrlKey || event.metaKey) {
                return;
            }

            const field = event.target.matches('input[type="text"]') ? event.target : null;
            const typed = event.key.length === 1 ? event.key : '';
            const opens = typed !== '' || event.key === 'ArrowDown' || (event.key === 'Enter' && (!field || field.value.trim() === ''));

            if (!opens) {
                return;
            }

            event.preventDefault();
            openFromTrigger(trigger);

            if (typed === '') {
                return;
            }

            // The character was typed into the header field; the overlay field is what searches, so
            // it starts with that character once the searchbox script has moved the focus there.
            const input = overlay.querySelector('#ffSearchParam');
            if (input) {
                window.setTimeout(() => {
                    input.value = typed;
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                    input.setSelectionRange(input.value.length, input.value.length);
                }, 0);
            }
        });

        // Escape closes the overlay and hands the focus back to the header field it came from.
        document.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape' || !document.body.classList.contains('kussin-factfinder-search-overlay-active')) {
                return;
            }

            const close = document.querySelector('.kussin-factfinder-search-overlay-close');
            if (close) {
                event.preventDefault();
                close.click();
                document.querySelector('.kussin-factfinder-search-overlay-trigger input[type="text"]')?.focus();
            }
        });
    }

    function initFactFinderMobileAsn() {
        const overlay = document.getElementById('kussinFactFinderMobileAsnOverlay');
        const triggers = Array.from(document.querySelectorAll('[data-kussin-factfinder-mobile-asn-trigger]'));

        if (!overlay || triggers.length === 0 || overlay.dataset.kussinFactfinderBound === '1') {
            return;
        }

        overlay.dataset.kussinFactfinderBound = '1';

        const dialog = overlay.querySelector('.kussin-factfinder-asn-overlay-dialog');
        const closeButton = overlay.querySelector('.kussin-factfinder-asn-overlay-close');
        const title = overlay.querySelector('[data-kussin-factfinder-asn-overlay-title]');
        const categoryTrigger = triggers.find(
            (trigger) => trigger.dataset.kussinFactfinderMobileAsnTrigger === 'category',
        );
        const mobileViewport = window.matchMedia('(max-width: 991.98px)');
        let activeTrigger = null;
        let lockedScrollPosition = null;

        const lockPageScroll = () => {
            if (lockedScrollPosition) {
                return;
            }

            lockedScrollPosition = {
                x: window.scrollX,
                y: window.scrollY,
            };

            document.documentElement.style.setProperty(
                '--kussin-factfinder-asn-scroll-x',
                `${lockedScrollPosition.x}px`,
            );
            document.documentElement.style.setProperty(
                '--kussin-factfinder-asn-scroll-y',
                `${lockedScrollPosition.y}px`,
            );
            document.body.classList.add('kussin-factfinder-asn-overlay-active');
            document.documentElement.classList.add('kussin-factfinder-asn-overlay-active');
        };

        const unlockPageScroll = () => {
            const position = lockedScrollPosition;
            lockedScrollPosition = null;

            document.body.classList.remove('kussin-factfinder-asn-overlay-active');
            document.documentElement.classList.remove('kussin-factfinder-asn-overlay-active');
            document.documentElement.style.removeProperty('--kussin-factfinder-asn-scroll-x');
            document.documentElement.style.removeProperty('--kussin-factfinder-asn-scroll-y');

            if (position) {
                window.scrollTo(position.x, position.y);
            }
        };

        const isCategoryGroup = (group) => group.getAttribute('for-group') === 'CategoryPathROOT';

        const categoryGroup = () => overlay.querySelector(
            '.kussin-factfinder-asn ff-asn-group[for-group="CategoryPathROOT"]',
        );

        const firstFilterGroup = () => Array.from(overlay.querySelectorAll(
            '.kussin-factfinder-asn ff-asn-group, '
            + '.kussin-factfinder-asn ff-asn-group-slider',
        )).find((group) => group.getAttribute('for-group') !== 'CategoryPathROOT');

        const refreshCategoryTrigger = () => {
            if (categoryTrigger) {
                categoryTrigger.hidden = !categoryGroup();
            }
        };

        const focusGroup = (mode) => {
            const group = mode === 'category' ? categoryGroup() : firstFilterGroup();

            if (!group) {
                closeButton?.focus();
                return;
            }

            group.scrollIntoView({ block: 'start' });

            const focusTarget = group.querySelector(
                '[data-container="groupCaption"] [role="button"], '
                + '.groupCaption[role="button"], '
                + '[slot="groupCaption"][tabindex]',
            );

            (focusTarget || closeButton)?.focus();
        };

        const closeOverlay = () => {
            returnCloud();
            overlay.classList.remove('is-active');
            overlay.setAttribute('aria-hidden', 'true');
            overlay.removeAttribute('data-kussin-factfinder-active-mode');

            if (dialog) {
                dialog.removeAttribute('role');
                dialog.removeAttribute('aria-modal');
                dialog.removeAttribute('aria-label');
            }

            triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

            if (activeTrigger) {
                const triggerToFocus = activeTrigger;
                activeTrigger = null;
                triggerToFocus.focus({ preventScroll: true });
            }

            unlockPageScroll();

            // #70644 — the listing waits for this before it glides to the new results.
            document.dispatchEvent(new CustomEvent('kussin-factfinder-asn-overlay-closed'));
        };

        const syncResponsiveState = () => {
            if (mobileViewport.matches) {
                if (!overlay.classList.contains('is-active')) {
                    overlay.setAttribute('aria-hidden', 'true');
                }

                return;
            }

            returnCloud();
            overlay.classList.remove('is-active');
            overlay.removeAttribute('aria-hidden');
            overlay.removeAttribute('data-kussin-factfinder-active-mode');
            triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
            activeTrigger = null;

            if (dialog) {
                dialog.removeAttribute('role');
                dialog.removeAttribute('aria-modal');
                dialog.removeAttribute('aria-label');
            }

            unlockPageScroll();
        };

        /*
         * #70138 — the drawer's own furniture: result count, selected-filter chips, collapsed
         * groups and the two footer buttons. Everything is filled from what the listing already
         * renders, so the page never carries a second `ff-filter-cloud` or a second result
         * template — two of either would both bind to the FactFinder bundle and fight over the
         * same state.
         */
        const countSlot = overlay.querySelector('[data-kussin-factfinder-asn-overlay-count]');
        const cloudSlot = overlay.querySelector('[data-kussin-factfinder-asn-overlay-cloud]');
        const moreButton = overlay.querySelector('[data-kussin-factfinder-asn-overlay-more]');
        const applyButton = overlay.querySelector('[data-kussin-factfinder-asn-overlay-apply]');
        const cloudHome = document.querySelector('.ff-filtercloud-wrapper');
        const VISIBLE_GROUPS = 4;

        /* Every group, category included: production lists "Kategorie" collapsed like the rest. */
        const asnGroups = () => Array.from(overlay.querySelectorAll(
            '.kussin-factfinder-asn ff-asn-group, .kussin-factfinder-asn ff-asn-group-slider',
        ));

        /* Mirror the listing's live result count into the header, the way production shows it. */
        const syncCount = () => {
            if (!countSlot) {
                return;
            }
            const source = document.querySelector('.kussin-factfinder-list-heading .kussin-factfinder-resultcount');
            const text = source ? source.textContent.replace(/\s+/g, ' ').trim() : '';
            countSlot.textContent = text;
            countSlot.hidden = text === '';
        };

        /* Borrow the one filter cloud while the drawer is open, hand it back on close. */
        const takeCloud = () => {
            const cloud = cloudHome && cloudHome.querySelector('ff-filter-cloud');
            if (!cloudSlot || !cloud) {
                return;
            }
            cloudSlot.appendChild(cloud);
            cloudSlot.hidden = false;
            markCategoryChips();
        };

        const returnCloud = () => {
            const cloud = cloudSlot && cloudSlot.querySelector('ff-filter-cloud');
            if (cloud && cloudHome) {
                cloudHome.appendChild(cloud);
            }
            if (cloudSlot) {
                cloudSlot.hidden = true;
            }
        };

        /*
         * #70470 — inside the "Filter" drawer the category is not a filter.
         *
         * The one filter cloud lists the category path ("Kategorie: Water", "Kategorie: Wake")
         * next to the real filters, and that is right everywhere else: on the desktop rail and in
         * the "Kategorie" drawer the visitor sees where they are and can step out. The "Filter"
         * drawer no longer lists the category tree at all, so its chips would be the only trace of
         * it there, and a listing narrowed by nothing but its own category would announce active
         * filters it does not have. So the chips are marked here and `asn.less` takes the marked
         * ones off the page in that one mode, together with the cloud's title when nothing else is
         * selected.
         *
         * A chip is a category chip when its name is a segment of the selected category path. The
         * path comes from the listing's own seed (`ff-communication add-params`, the way the
         * category page hands its path to FactFinder on first load) and from the `filter=` url
         * parameters FactFinder writes on every later navigation; the captions of the other facets
         * guard the rare case of a filter value spelled like a category. FactFinder does not fill
         * template placeholders into attributes, so the chip markup itself cannot say which group
         * it belongs to.
         */
        const categoryPathNames = () => {
            const names = new Set();
            const collect = (value) => {
                String(value || '').split(',').forEach((param) => {
                    let decoded = param;
                    try {
                        decoded = decodeURIComponent(param.replace(/\+/g, ' '));
                    } catch (error) {
                        return;
                    }
                    const match = decoded.match(/^filter=CategoryPath(?:ROOT)?:(.+)$/i);
                    if (match) {
                        match[1].split('/').forEach((name) => names.add(name.trim().toLowerCase()));
                    }
                });
            };
            const communication = document.querySelector('ff-communication');
            collect(communication ? communication.getAttribute('add-params') : '');
            new URLSearchParams(window.location.search).getAll('filter').forEach((filter) => collect(`filter=${filter}`));
            return names;
        };

        const markCategoryChips = () => {
            const cloud = document.querySelector('ff-filter-cloud');
            if (!cloud) {
                return;
            }
            const names = categoryPathNames();
            const otherCaptions = new Set(asnGroups()
                .filter((group) => !isCategoryGroup(group))
                .map((group) => {
                    const caption = group.querySelector('[data-container="groupCaption"], .groupCaption, [slot="groupCaption"]');
                    return caption ? caption.textContent.trim() : '';
                })
                .filter(Boolean));
            let others = 0;
            cloud.querySelectorAll('.kussin-factfinder-filtercloud-item').forEach((chip) => {
                const name = chip.querySelector('.kussin-factfinder-filtercloud-name');
                const group = chip.querySelector('.kussin-factfinder-filtercloud-group');
                const isCategory = !!name
                    && names.has(name.textContent.trim().toLowerCase())
                    && !(group && otherCaptions.has(group.textContent.trim()));
                chip.classList.toggle('is-category', isCategory);
                if (!isCategory) {
                    others += 1;
                }
            });
            cloud.classList.toggle('is-category-only', others === 0);
        };

        /* FactFinder re-renders the cloud on every filter change; mark again whenever it does. */
        const cloudObserver = new MutationObserver(() => window.requestAnimationFrame(markCategoryChips));
        const observeCloud = () => {
            const cloud = document.querySelector('ff-filter-cloud');
            if (cloud) {
                cloudObserver.observe(cloud, { childList: true, subtree: true });
                markCategoryChips();
            }
        };
        observeCloud();

        /*
         * Production opens the drawer with every group closed and only the first four listed,
         * behind "show all filters". The bundle writes an open group as `opened` on `.ffw-wrapper`
         * with no inline height, so the same spelling is used here and the caption toggle keeps
         * working afterwards.
         */
        const collapseGroups = (mode) => {
            /*
             * #70138 — the whole pass runs with the group transitions switched off, so the drawer
             * is drawn already settled instead of folding itself up in front of the visitor.
             */
            overlay.classList.add('is-collapsing');

            /*
             * #70138 — reveal everything before collapsing anything.
             *
             * The pass below closes a group by clicking its caption, and the same loop used to hide
             * the groups past the fold. On every open after the first, groups 5+ were still carrying
             * `is-beyond-fold` from last time, so their caption was `display: none` when it was
             * clicked and the component never recorded the toggle. Un-hide first, so every caption
             * clicked here is a laid-out element; the fold is re-applied at the end.
             */
            asnGroups().forEach((group) => group.classList.remove('is-beyond-fold'));

            asnGroups().forEach((group, index) => {
                const isCategory = isCategoryGroup(group);

                /*
                 * #70470 — the two buttons opened the same list: "Kategorie" and "Filter" both
                 * showed the category tree above the same four filters, the only difference being
                 * whether the tree stood open. The "Filter" drawer now leaves the tree out (the
                 * mode rule in asn.less takes it off the page), and a caption with no layout cannot
                 * record a toggle, so the group is not touched here — whatever state it is in is
                 * sorted out the next time the category drawer opens.
                 */
                if (isCategory && mode !== 'category') {
                    return;
                }

                const wrapper = group.querySelector('.ffw-wrapper');
                /* the category drawer is opened to browse the tree — leave that one group open */
                const keepOpen = isCategory && mode === 'category';

                /*
                 * Any caption, not only the ones the bundle marks `role="button"`. The category
                 * tree and the price slider render an inert-looking caption (plain <div>, no role,
                 * no tabindex) once a category filter is active — but it still toggles when
                 * clicked, so it is closed the same way as every other group.
                 */
                const caption = group.querySelector('[data-container="groupCaption"] > *');

                /*
                 * #70138 — a group can arrive already out of step with itself.
                 *
                 * The component records "expanded" twice: `opened` on the `ff-asn-group` host and
                 * `opened` on its `.ffw-wrapper`. On the inline-skates listing every group past the
                 * first four arrives with the attribute on the HOST only, wrapper closed and height
                 * zero — so the group reads as expanded while nothing is shown, and the caption
                 * toggle then answers the first tap by "closing" what is already closed. Tapping
                 * did nothing at all, however many times, which is what the ticket reports; the
                 * groups the drawer lists straight away are unaffected because they carry no such
                 * attribute.
                 *
                 * Clicking the caption cannot repair it — verified, three clicks changed nothing.
                 * The stale attribute has to come off the host, and then the component's own toggle
                 * works normally (verified: open 54 -> 84px with `opened` back on the wrapper, and
                 * closed again on the next tap).
                 */
                if (wrapper && !keepOpen && wrapper.hasAttribute('opened')) {
                    /*
                     * Let the bundle close it. Writing the closed state by hand
                     * (`height: 0; overflow: hidden`, no `opened`) LOOKS identical, but the
                     * component never recorded the transition, so its own toggle then refused to
                     * re-open the group — which is why a group had to be opened and closed once
                     * before the drawer behaved.
                     */
                    /*
                     * Always through the caption — never by writing the closed state onto the
                     * wrapper. Writing it by hand looks identical but leaves the component's own
                     * state untouched: the caret keeps pointing down (`opened` lives on the GROUP,
                     * the hand-written close only reached the wrapper) and the group can never be
                     * re-opened — "looks open, cannot be opened", which is what #70138 reported
                     * for Kategorie as soon as a category filter was active.
                     */
                    if (caption) {
                        caption.click();
                    }
                }

                /*
                 * #70138 — and then take the host's copy of the flag off.
                 *
                 * The component records "expanded" in two places: `opened` on the `ff-asn-group`
                 * host and `opened` on its `.ffw-wrapper`. Closing through the caption clears the
                 * wrapper's copy but leaves the host's behind, so the group ends up reading as
                 * expanded while its panel is closed — and the toggle then answers the next tap by
                 * "closing" what is already closed, i.e. nothing happens, however many times it is
                 * tapped. Measured on the inline-skates listing: eight of twelve groups sat in that
                 * state, and they are exactly the ones behind "Alle Filter anzeigen", because those
                 * are the ones this pass had to close.
                 *
                 * Clearing the host's copy repairs it — verified: the group then opens on the first
                 * tap (54 -> 84px, `opened` back on the wrapper) and closes again on the second.
                 */
                if (!keepOpen) {
                    group.removeAttribute('opened');
                }

                /*
                 * #70470 — the category drawer is the tree, so the tree has to stand open: a
                 * visitor who folded it on the last visit, or a listing whose facet arrived
                 * closed, otherwise got a drawer of collapsed rows. Opened the same way the others
                 * are closed, through the bundle's own caption; the host's stale copy of the flag
                 * comes off first, or the toggle answers the click by "closing" what is already
                 * closed (see above).
                 */
                if (wrapper && keepOpen && !wrapper.hasAttribute('opened')) {
                    group.removeAttribute('opened');

                    if (caption) {
                        caption.click();
                    }
                }
            });

            /*
             * #70138 — hide the groups past the fold only once they have finished closing.
             *
             * The fold is `display: none`. Applying it in the same pass that clicks a group shut
             * took the element out of layout while its 0.35s close was still running, so the
             * component recorded a natural height of zero for it. It then had nothing to animate
             * back to: the group reported itself open on the next tap — `opened` on both the host
             * and the wrapper — while the wrapper kept the inline `height: 0px; overflow: hidden`
             * it was left with, so only the caption and the filter search box were visible (54 ->
             * 84px instead of 54 -> 307px) and the values stayed clipped.
             *
             * Waiting out the transition before hiding lets each group record a real height, so it
             * opens properly the first time it is asked to. The drawer is covered by `is-preparing`
             * while this settles, so nothing flashes.
             */
            /*
             * #70470 — the fold counts filters only. The category tree is either the subject of
             * the drawer or not shown at all, so it neither takes one of the four slots nor ends
             * up behind "Alle Filter anzeigen".
             */
            asnGroups().filter((group) => !isCategoryGroup(group)).forEach((group, index) => {
                group.classList.toggle('is-beyond-fold', index >= VISIBLE_GROUPS);
            });

            /* read a layout property so the un-animated collapse is flushed before the class goes */
            void overlay.offsetHeight;
            overlay.classList.remove('is-collapsing');

            overlay.classList.remove('is-showing-all-groups');

            if (moreButton) {
                moreButton.hidden = asnGroups().filter((group) => !isCategoryGroup(group)).length <= VISIBLE_GROUPS;
            }
        };

        /*
         * #70138 — a running intro tour swallowed taps inside the drawer.
         *
         * On the Stoeckli brand page `kussin/oxid-intro-tours` auto-starts a tour that explains the
         * filter navigation, and one of its own steps opens this drawer. Its tooltip is an opaque,
         * interactive box roughly 260px tall laid over the panel: measured on a 390px viewport it
         * covered y 352-613, which is exactly the groups revealed by "Alle Filter anzeigen". The
         * four groups above it kept working, everything the tooltip covered did not — taps landed on
         * `.introjs-tooltiptext` instead of the group caption, so nothing opened and the panel looked
         * broken.
         *
         * Touching a filter is the clearest possible signal that the visitor no longer needs the
         * tour explaining filters, so the first interaction with the panel ends it — the usual
         * behaviour for a coach mark. The tour module keeps its live instances on
         * `window.kussinIntroTours`; exiting through the instance lets it run its own `onexit` and
         * remove its layers. The tooltip itself is excluded so its Next/Back/× still work.
         */
        const dismissIntroTour = (event) => {
            if (event.target instanceof Element
                && event.target.closest('.introjs-tooltip, .introjs-tooltipReferenceLayer')) {
                return;
            }

            const tours = window.kussinIntroTours;
            if (!tours) {
                return;
            }

            Object.keys(tours).forEach((id) => {
                const instance = tours[id] && tours[id].instance;
                if (instance && typeof instance.exit === 'function') {
                    instance.exit();
                }
            });
        };

        overlay.addEventListener('pointerdown', dismissIntroTour, true);

        const showAllGroups = () => {
            asnGroups().forEach((group) => group.classList.remove('is-beyond-fold'));
            overlay.classList.add('is-showing-all-groups');
            if (moreButton) {
                moreButton.hidden = true;
            }
        };

        const openOverlay = (trigger) => {
            const mode = trigger.dataset.kussinFactfinderMobileAsnTrigger;
            const label = mode === 'category'
                ? overlay.dataset.kussinFactfinderCategoryLabel
                : overlay.dataset.kussinFactfinderFilterLabel;

            activeTrigger = trigger;
            triggers.forEach((item) => item.setAttribute('aria-expanded', item === trigger ? 'true' : 'false'));

            lockPageScroll();
            overlay.classList.add('is-active');
            overlay.setAttribute('aria-hidden', 'false');
            overlay.dataset.kussinFactfinderActiveMode = mode;

            if (title) {
                title.textContent = label || '';
            }

            if (dialog) {
                dialog.setAttribute('role', 'dialog');
                dialog.setAttribute('aria-modal', 'true');
                dialog.setAttribute('aria-label', label || '');
            }

            /*
             * The collapse runs once the sheet is on screen: it closes each group through the
             * bundle's own caption toggle, and that toggle measures the element — on a
             * `display: none` dialog it measures zero and the group ends up open instead. The
             * sheet is held invisible for that frame by `is-preparing` so nothing flashes.
             */
            overlay.classList.add('is-preparing');
            syncCount();
            takeCloud();

            window.requestAnimationFrame(() => {
                collapseGroups(mode);
                window.requestAnimationFrame(() => overlay.classList.remove('is-preparing'));
                focusGroup(mode);
            });
        };

        moreButton?.addEventListener('click', showAllGroups);
        applyButton?.addEventListener('click', () => closeOverlay());
        document.addEventListener('dom-updated', () => {
            window.requestAnimationFrame(markCategoryChips);
            if (overlay.classList.contains('is-active')) {
                window.requestAnimationFrame(syncCount);
            }
        });

        triggers.forEach((trigger) => {
            trigger.addEventListener('click', () => openOverlay(trigger));
        });

        overlay.addEventListener('click', (event) => {
            if (
                event.target === overlay
                || (event.target.closest && event.target.closest('.kussin-factfinder-asn-overlay-close'))
            ) {
                closeOverlay();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && overlay.classList.contains('is-active')) {
                closeOverlay();
            }
        });

        document.addEventListener('WebComponentsReady', refreshCategoryTrigger);
        document.addEventListener('dom-updated', (event) => {
            if (event.target && event.target.matches && event.target.matches('ff-asn')) {
                refreshCategoryTrigger();

                if (
                    overlay.classList.contains('is-active')
                    && document.activeElement === closeButton
                ) {
                    focusGroup(overlay.dataset.kussinFactfinderActiveMode);
                }
            }
        });

        /*
         * #70737 — a tour step that points into the drawer opens it.
         *
         * On a phone the Stöckli tour explains the filters one group at a time, and every group
         * lives in this drawer. The tour only knows its selectors: the legacy `wmdkintrojs` widget
         * never opened the drawer, the `kussin/oxid-intro-tours` one clicks the filter button but
         * cannot see the fold, so the groups behind "Alle Filter anzeigen" stayed hidden. Either
         * way the tooltip floated over the listing with nothing highlighted.
         *
         * Intro.js marks the current target with `introjs-showElement` (both bundles do), so the
         * drawer watches for that class on its own contents: it opens itself with the filter
         * button, unfolds the group once `collapseGroups()` has had its frame, brings it into view
         * and lets the tour re-measure through the resize it listens to.
         */
        const filterTrigger = triggers.find(
            (trigger) => trigger.dataset.kussinFactfinderMobileAsnTrigger === 'filters',
        );
        const revealTourTarget = (target) => {
            if (!mobileViewport.matches || !filterTrigger) {
                return;
            }

            if (!overlay.classList.contains('is-active')) {
                openOverlay(filterTrigger);
            }

            window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
                if (target.classList.contains('is-beyond-fold')) {
                    showAllGroups();
                }
                target.scrollIntoView({ block: 'center' });
                window.dispatchEvent(new Event('resize'));
            }));
        };

        new MutationObserver((records) => {
            records.forEach((record) => {
                const target = record.target;
                if (
                    target.classList.contains('introjs-showElement')
                    && !String(record.oldValue || '').includes('introjs-showElement')
                ) {
                    revealTourTarget(target);
                }
            });
        }).observe(overlay, {
            subtree: true,
            attributes: true,
            attributeFilter: ['class'],
            attributeOldValue: true,
        });

        if (typeof mobileViewport.addEventListener === 'function') {
            mobileViewport.addEventListener('change', syncResponsiveState);
        } else if (typeof mobileViewport.addListener === 'function') {
            mobileViewport.addListener(syncResponsiveState);
        }
        refreshCategoryTrigger();
        syncResponsiveState();
    }

    /**
     * FactFinder product listing: scroll behaviour.
     *
     * Redmine #70077 / #70025 / #70038 and #70076. Both behaviours existed in the OXID 6 shop, in
     * `kussin/oxid-factfinder-integration/assets/js/factfinder.js` — a 35 KB file that no template
     * loads on OXID 7, so every helper in it is dead code. They are re-implemented here against the
     * current markup instead of reviving that file, which still targets Bootstrap 3 selectors and
     * reads globals (`WMDK_FF_*`) that nothing emits any more.
     */
    const FF_LISTING = '#kussin-factfinder-listing';
    const FF_RECORD_LIST = '.kussin-factfinder-record-list';
    const FF_FILTERCLOUD = '.ff-filtercloud-wrapper';

    /**
     * The element the "to products" chevron should land on.
     *
     * #70077: the static `#wh1-products` anchor sits at the end of the list head, i.e. ABOVE the
     * FactFinder toolbar and the "selected filters" cloud, so jumping to it left half the promo
     * teaser on screen.
     *
     * Aiming at the record list instead fixed that but overshot in the other direction: it hides
     * the "Ausgewählte Filter" block, so after the jump nothing on screen says which filters the
     * list is narrowed by. The ticket's second round asks for that block to be part of the
     * landing, so the target is the cloud's wrapper — the first thing in the results column, with
     * the first row of products directly underneath it.
     *
     * The wrapper is static markup and stays in place even when it holds nothing: with no filters
     * selected it collapses to zero height, so the jump then lands on the products exactly as
     * before. It also survives the mobile filter drawer borrowing the cloud out of it while open.
     *
     * The two fallbacks are for listings that have no cloud (the record list) and for the
     * non-FactFinder manufacturer/vendor lists (the static anchor).
     */
    function factFinderScrollTarget() {
        return (factFinderScrollAnchor && document.querySelector(factFinderScrollAnchor))
            || document.querySelector(FF_FILTERCLOUD)
            || document.querySelector(FF_RECORD_LIST)
            || document.getElementById('wh1-products');
    }

    /**
     * #70644 — where a filter change lands, as opposed to where the chevron lands.
     *
     * The chevron (#70077) stops on the selected-filters cloud so the promo teaser is gone and the
     * chips stay in view. After a filter click the visitor wants the heading with the new result
     * count first — that is what the OXID 6 shop scrolled to (`wmdkAsnScrollToListing()` aimed at
     * the sortbox row) — so that jump is anchored one block higher, on the list toolbar. Set for
     * the duration of one jump; every other caller leaves it empty and gets the cloud.
     */
    const FF_LIST_TOOLBAR = '.kussin-factfinder-list-toolbar';
    let factFinderScrollAnchor = null;

    /**
     * How much of the top of the viewport is covered right now.
     *
     * Desktop has nothing pinned, but mobile keeps a ~116px header there, and landing flush with
     * the record list would slide the first row of products underneath it. Reading the element
     * stack at the top edge finds whatever is actually covering that point, so this keeps working
     * if the header changes height or a bar is added later. Full-height elements are skipped: the
     * mega-menu's off-canvas panel is `position: fixed` over the whole viewport even while closed,
     * and treating it as a header would push the scroll a screen too far.
     */
    function pinnedTopOffset() {
        const stack = document.elementsFromPoint(Math.round(window.innerWidth / 2), 1) || [];
        let offset = 0;
        stack.forEach((element) => {
            const position = window.getComputedStyle(element).position;
            if (position !== 'fixed' && position !== 'sticky') {
                return;
            }
            const rect = element.getBoundingClientRect();
            if (rect.height > window.innerHeight * 0.3) {
                return;
            }
            offset = Math.max(offset, rect.bottom);
        });
        return offset;
    }

    /** Put the target's top edge just below whatever is pinned. Returns false when already there. */
    function alignToProducts(smooth) {
        const target = factFinderScrollTarget();
        if (!target) {
            return false;
        }
        const delta = Math.round(target.getBoundingClientRect().top - pinnedTopOffset());
        if (Math.abs(delta) < 2) {
            return false;
        }
        window.scrollBy({ top: delta, behavior: smooth ? 'smooth' : 'auto' });
        return true;
    }

    /**
     * #70077: aim, then check — the distance cannot be trusted at click time.
     *
     * The scroll offset is computed once, when the animation starts, but the content above the
     * record list is still settling: the promo teasers and category images up there load lazily and
     * FactFinder re-renders the list itself. Measured on /water/neopren/, the list sat at document
     * offset 2041 on one load and 1601 on the next, so a single `scrollIntoView` landed 440px past
     * the first row — the same symptom the ticket describes, in the other direction. Re-measuring
     * after the animation ends and correcting is what makes this hold on any category rather than
     * on the one it was tuned against.
     *
     * The correction stops as soon as the visitor touches the page, so it can never fight a manual
     * scroll, and it gives up after ~1s.
     */
    function scrollToProducts(behavior, anchor) {
        factFinderScrollAnchor = anchor || null;

        if (!factFinderScrollTarget()) {
            return;
        }

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reduced || behavior === 'auto') {
            alignToProducts(false);
            holdOnProducts();
            return;
        }

        glideToProducts();
    }

    /**
     * #70077, third round — the jump has to be one movement, not two.
     *
     * The earlier shape was "scroll smoothly, then measure and correct". On a desktop the
     * correction is nothing, because the page above the list has already settled by the time the
     * animation ends; on a phone it is not. Measured on /snow/snowwear/skijacken/ at 390px, the
     * viewport ran down to 1118 and was then yanked back up to 1002 — the two jumps the report
     * describes, because the correction was applied with `behavior: "auto"` after the smooth
     * animation had finished.
     *
     * So the destination is re-read on every frame of a single animation instead. The step is a
     * fifth of whatever distance is still left, which eases out on its own and absorbs the promo
     * teasers and the FactFinder re-render as they change the height above the list, rather than
     * discovering them afterwards. The visitor sees one glide that happens to end in the right
     * place.
     */
    function glideToProducts() {
        const events = ['wheel', 'touchstart', 'keydown'];
        const deadline = Date.now() + 3000;
        let cancelled = false;
        let settledFrames = 0;
        let frame = null;

        function stop() {
            events.forEach((name) => window.removeEventListener(name, cancel));
            if (frame) {
                window.cancelAnimationFrame(frame);
                frame = null;
            }
        }

        function cancel() {
            cancelled = true;
            stop();
        }

        function step() {
            if (cancelled) {
                return;
            }

            const target = factFinderScrollTarget();

            if (!target || Date.now() > deadline) {
                stop();
                holdOnProducts();
                return;
            }

            const delta = Math.round(target.getBoundingClientRect().top - pinnedTopOffset());

            if (Math.abs(delta) < 2) {
                // Hold still for a few frames before declaring it done: the content above the list
                // can settle between one frame and the next, and stopping on the first quiet frame
                // is what let the old code finish early and need a correction afterwards.
                settledFrames += 1;

                if (settledFrames > 12) {
                    stop();
                    holdOnProducts();
                    return;
                }
            } else {
                settledFrames = 0;
                // At least a pixel, so the last stretch cannot stall on rounding.
                const move = delta > 0
                    ? Math.max(1, Math.round(delta * 0.2))
                    : Math.min(-1, Math.round(delta * 0.2));
                window.scrollBy(0, move);
            }

            frame = window.requestAnimationFrame(step);
        }

        events.forEach((name) => window.addEventListener(name, cancel, { passive: true }));
        frame = window.requestAnimationFrame(step);
    }

    /**
     * Keep the list at the top while the page above it is still settling.
     *
     * Measured on /water/neopren/, the content above can lose 440px about two seconds after the
     * jump has finished, which drags the first product row up out of view while the viewport stays
     * where it is. This watches for that and glides back rather than snapping, so a late shift
     * reads as the page correcting itself instead of a second jump.
     */
    function holdOnProducts() {
        if (typeof ResizeObserver !== 'function') {
            return;
        }

        let observer = null;
        let correcting = false;

        const done = () => {
            if (observer) {
                observer.disconnect();
                observer = null;
            }
        };

        observer = new ResizeObserver(() => {
            const target = factFinderScrollTarget();

            if (!target || correcting) {
                return;
            }

            const delta = Math.round(target.getBoundingClientRect().top - pinnedTopOffset());

            if (Math.abs(delta) < 4) {
                return;
            }

            correcting = true;
            done();
            glideToProducts();
        });

        observer.observe(document.body);
        window.setTimeout(done, 4000);
    }

    /**
     * #70098 — act on several basket lines at once.
     *
     * The page already posts the whole basket as one form (`fnc=changebasket`), and APEX's own
     * `changeamount.min.js` removes a line by setting its quantity to zero and dispatching a
     * change. Both group actions ride on that rather than introducing a second mechanism:
     * "Entfernen" zeroes every ticked row and submits once, "Aktualisieren" simply submits whatever
     * quantities have been typed.
     *
     * The listeners are re-attached on `basketLoaded`, which APEX dispatches after it has swapped
     * the document body for the response — without that the controls are dead from the first
     * quantity change onwards.
     */
    function initBasketSelection() {
        const form = document.getElementById('basket_form');

        if (!form) {
            return;
        }

        const selectAll = form.querySelector('#kussinBasketSelectAll');
        const items = Array.from(form.querySelectorAll('.kussin-basket-select-item'));
        const removeButton = form.querySelector('[data-kussin-basket-action="remove"]');
        const updateButton = form.querySelector('[data-kussin-basket-action="update"]');

        if (items.length === 0) {
            return;
        }

        const selected = () => items.filter((item) => item.checked);

        const syncState = () => {
            const count = selected().length;

            if (removeButton) {
                removeButton.disabled = count === 0;
            }

            if (selectAll) {
                selectAll.checked = count === items.length;
                selectAll.indeterminate = count > 0 && count < items.length;
            }
        };

        // Submitting through one row's amount input keeps the whole flow — the loading state, the
        // fetch, the body swap — in APEX's hands instead of duplicating it here.
        const submitBasket = () => {
            const anyAmount = form.querySelector('input[name^="aproducts"][name$="[am]"]');

            if (anyAmount) {
                anyAmount.dispatchEvent(new Event('change', { bubbles: true }));
            } else {
                form.submit();
            }
        };

        items.forEach((item) => item.addEventListener('change', syncState));

        if (selectAll) {
            selectAll.addEventListener('change', () => {
                items.forEach((item) => { item.checked = selectAll.checked; });
                syncState();
            });
        }

        if (removeButton) {
            removeButton.addEventListener('click', () => {
                const rows = selected();

                if (rows.length === 0) {
                    return;
                }

                rows.forEach((row) => {
                    const amount = form.querySelector(
                        'input[name="aproducts[' + row.dataset.kussinBasketRow + '][am]"]',
                    );

                    if (amount) {
                        amount.value = 0;
                    }
                });

                submitBasket();
            });
        }

        if (updateButton) {
            updateButton.addEventListener('click', submitBasket);
        }

        syncState();
    }

    /**
     * #70292 — group every checkout step into production's panels.
     *
     * Each step arrives as a flat run: a heading, then its fields, then the next heading. There is
     * nothing for CSS to paint a panel on, so the run is walked once and each heading is wrapped
     * together with everything up to the next one. The rest of the treatment is stylesheet.
     *
     * The roots differ per step because the markup does — the address form is one container, the
     * payment step splits into a payment and a shipping form, and the order step keeps its two
     * addresses in `#orderAddress` with the remaining sections as siblings of it inside the card.
     * Walking `#orderAddress` and the card separately is deliberate: the card's own walk only
     * starts once it meets a heading, so `#orderAddress` passes it by untouched and no panel ends
     * up inside another.
     */
    function panelizeCheckoutSection(root) {
        if (!root || root.dataset.kussinPanels === '1') {
            return;
        }

        if (!root.querySelector(':scope > h2')) {
            return;
        }

        root.dataset.kussinPanels = '1';

        let panel = null;

        // A heading starts a panel, and so does a block that carries a heading of its own: the order
        // step keeps its cart inside `#orderEditCart` and the coupon box in a card, and both would
        // otherwise be swallowed by whichever panel happened to be open, which put the cart and the
        // coupon field inside the message panel.
        const startsPanel = (node) =>
            node.tagName === 'H2' || (node.querySelector && node.querySelector('h2') !== null);

        Array.from(root.children).forEach((node) => {
            // A block that has already been panelled, by an earlier root, is left where it is: the
            // order step walks `#orderAddress` and then the card around it, and wrapping the first
            // result a second time would put a panel inside a panel.
            if (node.classList.contains('kussin-checkout-panel')
                || node.querySelector('.kussin-checkout-panel') !== null) {
                panel = null;

                return;
            }

            if (startsPanel(node)) {
                panel = document.createElement('div');
                panel.className = 'kussin-checkout-panel';
                root.insertBefore(panel, node);
            }

            if (panel) {
                panel.appendChild(node);
            }
        });
    }

    function initCheckoutPanels() {
        const orderAddress = document.getElementById('orderAddress');

        [
            document.getElementById('addressForm'),
            // The shipping-address block is a sibling of the address form inside the same form
            // element, so it is picked up by walking the form itself: the walk only starts at the
            // first heading it meets, which leaves `#addressForm` above it untouched.
            document.getElementById('user_form'),
            document.getElementById('payment'),
            document.getElementById('shipping'),
            orderAddress,
            orderAddress ? orderAddress.closest('.card') : null,
        ].forEach(panelizeCheckoutSection);
    }

    /**
     * #70292 — repeat the step's back and forward controls above the form.
     *
     * Production puts them at both ends of every checkout step; APEX has the back link at the foot
     * of the left column and the forward button down in the sidebar, so on a phone the customer
     * scrolls the whole address form before finding either. The originals stay where they are and
     * keep doing the work: the copy up here is a plain link for "back" and a button that clicks the
     * real submit for "forward", so there is one submit handler on the page and no duplicated id.
     */
    /**
     * #70098 — the fourth step marker, clicked from the payment step.
     *
     * The marker's href points at the confirmation page, which is where it leads from every other
     * step. From the payment step itself that would skip the shipping and payment choice the
     * customer has just made, so the click is answered by submitting the payment form instead —
     * the shop then validates the choice and moves on by itself. Production does the same thing
     * with a `javascript:` href; the href here stays a real URL so the marker is still a link when
     * the script does not run.
     */
    function initCheckoutStepSubmit() {
        document.addEventListener('click', (event) => {
            const link = event.target.closest('[data-kussin-step-submit]');

            if (!link) {
                return;
            }

            const form = document.getElementById(link.dataset.kussinStepSubmit);

            if (!form) {
                return;
            }

            event.preventDefault();

            if (form.requestSubmit) {
                form.requestSubmit();
            } else {
                form.submit();
            }
        });
    }

    /* #70333 / #70334 — the step's back and forward controls are NOT repeated above the form.

       #70292 added a bar that cloned them there, on the grounds that APEX buries both controls
       below a form the customer has to scroll past. The customer read the result the other way:
       the confirm button appeared twice on the address step and the back link was one control too
       many, and on the payment step the cloned bar landed inside the PayPal Express panel. The
       step keeps the single pair APEX renders. */


    /* ===================================================================
     * #70313 — the header widgets are personalised in the browser, not on the server.
     *
     * Cloudflare caches the finished HTML document and does not vary on cookies, so anything the
     * server prints into the header is handed to the next visitor who lands on that cached copy.
     * The mini basket and the account menu are exactly that: a basket count and a customer's own
     * notification counts. On www1 a cached category page was measured at
     * `Cf-Cache-Status: HIT, Age: 19`, so this is not theoretical.
     *
     * The page therefore ships the ANONYMOUS widget — real markup, real icons, nothing personal,
     * correct for a shared document and correct with JavaScript off — and each wrapper is
     * re-fetched here for the visitor who is actually looking at it.
     *
     * This is Jan-Ole's OXID 6 solution (#36246, `wmdkReloadMiniBasket` / `wmdkReloadMiniAccount`
     * in wh1-2023) carried over, with two changes. That version fetched with jQuery, which this
     * storefront does not have, and it rendered the widget PERSONALISED and then replaced it —
     * which under an edge cache leaks the previous visitor's basket into the document and shows it
     * until the swap lands. Rendering it anonymous removes both problems.
     *
     * `nocookie=1` and the timestamp are his, and both matter: without them the fragment itself is
     * a cacheable GET on a URL every visitor shares.
     * =================================================================== */
    /* What each wrapper last received, keyed by widget and parameters. APEX's basket page swaps the
       whole <body> for the fetched document after every quantity change (changeamount.js), and the
       document it swaps in carries the anonymous placeholders again. This is what puts the visitor's
       own header back without a round trip. */
    var liveHeaderWidgetMarkup = {};

    function applyLiveHeaderWidget(wrapper, widget, markup) {
        wrapper.innerHTML = markup;
        wrapper.setAttribute('data-kussin-live-loaded', '');

        /* The flyout arrives marked when an article has just gone into the basket (see
           widget/header/minibasket.html.twig). Open it once, and drop the mark before the markup is
           remembered, so a restore after APEX's body swap does not open it again. */
        var flyout = wrapper.querySelector('#basketModal[data-kussin-flyout-open]');

        if (flyout) {
            flyout.removeAttribute('data-kussin-flyout-open');

            if (window.bootstrap && window.bootstrap.Modal) {
                window.bootstrap.Modal.getOrCreateInstance(flyout).show();
            }
        }

        /* Anything that wires itself to the header has to be told: the nodes it bound
           to have just been replaced. */
        window.dispatchEvent(new CustomEvent('kussinHeaderWidgetLoaded', {
            detail: { widget: widget, wrapper: wrapper }
        }));
    }

    /**
     * @param {string} [refreshWidget] a widget class whose state is known to have changed. Its
     *   wrappers are fetched again even when they already hold this visitor's markup; every other
     *   wrapper is restored from memory and left alone.
     */
    function initLiveHeaderWidgets(refreshWidget) {
        var wrappers = document.querySelectorAll('[data-kussin-live-widget]');

        if (!wrappers.length || typeof window.fetch !== 'function') {
            return;
        }

        var base = (window.WH1_SHOP_URL || '/').replace(/\/+$/, '/') || '/';
        var lang = document.documentElement.getAttribute('lang') === 'en' ? 1 : 0;

        Array.prototype.forEach.call(wrappers, function (wrapper) {
            var widget = wrapper.getAttribute('data-kussin-live-widget');
            var params = wrapper.getAttribute('data-kussin-live-params') || '';
            var key = widget + '?' + params;
            var cached = liveHeaderWidgetMarkup[key];
            var settled = wrapper.hasAttribute('data-kussin-live-loaded')
                || wrapper.hasAttribute('data-kussin-live-pending');

            if (settled && widget !== refreshWidget) {
                return;
            }

            /* A fresh wrapper after a body swap: show what this visitor already saw, at once. The
               basket widgets are then fetched again below because the basket is what just changed;
               the account menu is not, so its restored markup is final. */
            if (cached && !wrapper.hasAttribute('data-kussin-live-loaded')) {
                applyLiveHeaderWidget(wrapper, widget, cached);
            }

            if (cached && widget !== refreshWidget) {
                return;
            }

            var url = base + 'widget.php?cl=' + encodeURIComponent(widget)
                + '&lang=' + lang
                + '&nocookie=1&time=' + Date.now()
                + (params ? '&' + params : '');

            wrapper.setAttribute('data-kussin-live-pending', '');

            fetch(url, {
                // the session cookie is the whole point: without it the shop answers anonymously
                credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            })
                .then(function (response) {
                    return response.ok ? response.text() : null;
                })
                .then(function (html) {
                    wrapper.removeAttribute('data-kussin-live-pending');

                    if (html === null) {
                        return;
                    }

                    var markup = html.trim();

                    /* An empty answer means the shop had nothing to say for this visitor; the
                       anonymous widget already on the page stays, which is the right fallback. */
                    if (!markup) {
                        return;
                    }

                    applyLiveHeaderWidget(wrapper, widget, markup);
                    liveHeaderWidgetMarkup[key] = wrapper.innerHTML;
                })
                .catch(function () {
                    /* Offline, blocked, or a 500: the anonymous widget stays on the page rather
                       than the header emptying out. */
                    wrapper.removeAttribute('data-kussin-live-pending');
                });
        });
    }

    function initFactFinderListing() {
        // ---- #70077 / #70025 / #70038 — the hero chevron ----------------------------------
        // Handled in JS rather than by pointing the href at the record list, because FactFinder
        // renders that list client-side: at parse time the element does not exist yet, so a plain
        // `href="#…"` would resolve to nothing on the first click.
        document.querySelectorAll('a.wh1-cat-hero-link, a[href="#wh1-products"]').forEach((link) => {
            link.addEventListener('click', (event) => {
                if (!factFinderScrollTarget()) {
                    return; // let the browser handle it
                }
                event.preventDefault();
                scrollToProducts('smooth');
            });
        });

        /*
         * #70038 — the CMS "jump to products" anchor.
         *
         * The record itself carries `onclick="wmdkAsnScrollToListing();"`, an OXID 6 helper that
         * lived in the FactFinder module's `assets/js/factfinder.js` — a file no template in this
         * shop loads, so the call hit an undefined function and the link did nothing. The styling
         * was fixed on this ticket; this is the other half.
         *
         * The name is published rather than the anchor rebound, because the call sits in CMS content
         * that editors maintain: every record already using it starts working, and one that is
         * copied to another page keeps working, without touching the content.
         *
         * It deliberately shares `scrollToProducts` with the hero chevron (#70077), so both land in
         * the same place — below whatever is pinned at the top, re-aligned once the content above
         * the list has settled.
         */
        window.wmdkAsnScrollToListing = function () {
            scrollToProducts('smooth');
        };

        // ---- #70076 — pagination must reset the scroll position --------------------------
        // Clicking "next page" left the viewport at the bottom of the previous page. FactFinder
        // replaces the record list in place and fires `dom-updated` on the element it re-rendered,
        // so wait for that rather than guessing a timeout — the results are not on screen yet when
        // the click handler runs.
        const listing = document.querySelector(FF_LISTING);
        if (!listing) {
            return;
        }

        let pagingClicked = false;

        /*
         * #70644 — a filter change must bring the listing back into view.
         *
         * FactFinder applies an ASN or filter-cloud click in place: it rewrites the URL, swaps the
         * record list and fires `dom-updated` on it, and the viewport stays wherever it was. With a
         * long filter column that is somewhere in the middle of the brand list, with the heading
         * and the first products off screen — on production the OXID 6 helper glided the page to
         * the heading after every change, so the new result count and the first row were the next
         * thing the visitor saw. The same glide is re-used here, anchored on the toolbar.
         *
         * The flag is only raised by a click inside the filter widgets, so the initial render and
         * the pagination path above are untouched, and it is consumed by the first record-list
         * update that follows, so a click that changes nothing (an already selected value, a
         * caption toggle) leaves the page alone until a real change comes.
         *
         * On a phone the filters sit in the drawer, where the page scroll is locked and restored
         * on close: a glide behind the sheet would be undone the moment it closes. The jump is
         * held until the drawer reports it has closed (`kussin-factfinder-asn-overlay-closed`),
         * which is also when the OXID 6 shop did it (`bMobileFilterClosedState`).
         */
        let filterClicked = false;
        let scrollWhenOverlayCloses = false;
        const overlayIsOpen = () => Boolean(
            document.querySelector('.kussin-factfinder-asn-overlay.is-active'),
        );

        // Capture phase: the ASN re-renders itself synchronously in its own click handler, so by
        // the time the event bubbles up here the clicked node is already detached and no longer
        // has `ff-asn` above it. Looking before FactFinder acts sees the real ancestry.
        listing.addEventListener('click', (event) => {
            if (!event.target.closest) {
                return;
            }
            if (event.target.closest('.kussin-factfinder-pagination')) {
                pagingClicked = true;
            } else if (event.target.closest('ff-asn, ff-filter-cloud')) {
                filterClicked = true;
            }
        }, true);

        document.addEventListener('dom-updated', (event) => {
            const target = event.target;
            if (!target || !target.matches || !target.matches('ff-record-list')) {
                return;
            }
            if (pagingClicked) {
                pagingClicked = false;
                filterClicked = false;
                // The record list re-renders its children right after the event; let it paint
                // first so the scroll lands on the final layout rather than a half-built one.
                window.requestAnimationFrame(() => scrollToProducts('auto'));
                return;
            }
            if (!filterClicked) {
                return;
            }
            filterClicked = false;
            if (overlayIsOpen()) {
                scrollWhenOverlayCloses = true;
                return;
            }
            window.requestAnimationFrame(() => scrollToProducts('smooth', FF_LIST_TOOLBAR));
        });

        document.addEventListener('kussin-factfinder-asn-overlay-closed', () => {
            if (!scrollWhenOverlayCloses) {
                return;
            }
            scrollWhenOverlayCloses = false;
            // The close handler restores the locked scroll position first; start after that.
            window.requestAnimationFrame(() => scrollToProducts('smooth', FF_LIST_TOOLBAR));
        });
    }

    /**
     * #70058 — attach the explanatory tooltips to the ASN filter group headings.
     *
     * The map comes from `{sShopDir}/out/asntooltips/{lang}.csv` via
     * `ViewConfig::getFFAsnTooltips()`, keyed by the lowercased group label — the same contract the
     * OXID 6 shop used. Production does this with jQuery and a Font Awesome icon; neither exists in
     * this theme, so the marker is a plain button with an inline glyph, and the text is exposed
     * through `title` + `aria-label` so it is reachable by keyboard and screen reader rather than
     * hover-only.
     */
    function initFactFinderAsnTooltips() {
        const tooltips = window.KUSSIN_FACTFINDER_ASN_TOOLTIPS;
        if (!tooltips || typeof tooltips !== 'object') {
            return;
        }

        /*
         * #70058 — the tooltips belong on the filter *values*, not on the group headings.
         *
         * The CSV behind `KUSSIN_FACTFINDER_ASN_TOOLTIPS` is a flat label -> text map and carries
         * keys for both kinds of label, so an implementation that looks up group captions finds
         * plenty of matches and looks correct in isolation. Measured against production on /snow/
         * the two sets turned out to be disjoint: production marks 14 values (Audio, Step In, Soft,
         * Medium, Hard, Twin Tip, …) and no caption at all, so keying off captions dropped every
         * tooltip the shop is supposed to show and invented fifteen it never had.
         */
        const bubbleId = 'wh1-asn-tooltip-bubble';
        let bubble = null;
        let openMarker = null;

        const getBubble = () => {
            if (bubble) {
                return bubble;
            }
            bubble = document.createElement('div');
            bubble.id = bubbleId;
            bubble.className = 'wh1-asn-tooltip-bubble';
            bubble.setAttribute('role', 'tooltip');
            bubble.hidden = true;
            bubble.innerHTML = '<span class="wh1-asn-tooltip-caret"></span>'
                + '<span class="wh1-asn-tooltip-text"></span>';
            document.body.appendChild(bubble);
            return bubble;
        };

        const place = () => {
            if (!openMarker || !bubble) {
                return;
            }
            const anchor = openMarker.getBoundingClientRect();
            const gap = 10;

            // Measure first, then clamp into the viewport — the ASN sits in a narrow column and a
            // left-aligned bubble would otherwise hang off the edge on the last filter group.
            bubble.style.left = '0px';
            bubble.style.top = '0px';
            const size = bubble.getBoundingClientRect();
            const margin = 8;
            let left = anchor.left + anchor.width / 2 - size.width / 2;
            left = Math.max(margin, Math.min(left, window.innerWidth - size.width - margin));

            let top = anchor.bottom + gap;
            let above = false;
            if (top + size.height > window.innerHeight - margin && anchor.top - gap - size.height > margin) {
                top = anchor.top - gap - size.height;
                above = true;
            }

            bubble.style.left = `${Math.round(left)}px`;
            bubble.style.top = `${Math.round(top)}px`;
            bubble.classList.toggle('is-above', above);

            const caret = bubble.querySelector('.wh1-asn-tooltip-caret');
            if (caret) {
                caret.style.left = `${Math.round(anchor.left + anchor.width / 2 - left)}px`;
            }
        };

        const hide = () => {
            if (!openMarker) {
                return;
            }
            openMarker.removeAttribute('aria-describedby');
            openMarker.setAttribute('aria-expanded', 'false');
            openMarker = null;
            if (bubble) {
                bubble.hidden = true;
            }
        };

        const show = (marker) => {
            const text = marker.getAttribute('data-tooltip');
            if (!text) {
                return;
            }
            const element = getBubble();
            element.querySelector('.wh1-asn-tooltip-text').textContent = text;
            element.hidden = false;
            openMarker = marker;
            marker.setAttribute('aria-describedby', bubbleId);
            marker.setAttribute('aria-expanded', 'true');
            place();
        };

        const makeMarker = (text) => {
            const marker = document.createElement('button');
            marker.type = 'button';
            marker.className = 'wh1-asn-tooltip';
            marker.setAttribute('data-tooltip', text);
            marker.setAttribute('aria-label', text);
            marker.setAttribute('aria-expanded', 'false');
            // Production's icon is a solid FontAwesome info-circle; this theme ships no icon font.
            marker.innerHTML =
                '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">'
                + '<circle cx="8" cy="8" r="8" fill="currentColor"/>'
                + '<rect x="7.1" y="6.6" width="1.8" height="5.6" rx="0.9" fill="#fff"/>'
                + '<circle cx="8" cy="4.3" r="1.1" fill="#fff"/></svg>';

            /*
             * Hover is for pointing devices only. A tap synthesises `mouseenter` before the click,
             * so binding it unconditionally made the touch sequence open the bubble and then
             * immediately toggle it shut again — the marker looked dead on a phone. Touch and pen
             * go through the click handler below, which toggles and leaves it open until the next
             * tap.
             */
            marker.addEventListener('pointerenter', (event) => {
                if (event.pointerType === 'mouse') {
                    show(marker);
                }
            });
            marker.addEventListener('pointerleave', (event) => {
                if (event.pointerType === 'mouse') {
                    hide();
                }
            });
            marker.addEventListener('focus', () => show(marker));
            marker.addEventListener('blur', hide);

            return marker;
        };

        const attach = () => {
            document.querySelectorAll('ff-asn-group-element').forEach((element) => {
                // A value renders either as the selected or the unselected variant; `.filterName` is
                // the label inside the checkbox groups, the bare slot div is used by the rest.
                let labels = element.querySelectorAll('.filterName');
                if (!labels.length) {
                    labels = element.querySelectorAll('[slot="unselected"], [slot="selected"]');
                }

                labels.forEach((label) => {
                    if (label.querySelector('.wh1-asn-tooltip')) {
                        return;
                    }
                    const text = tooltips[label.textContent.trim().toLowerCase()];
                    if (!text) {
                        return;
                    }
                    label.appendChild(makeMarker(text));
                });
            });
        };

        attach();

        // FactFinder re-renders the ASN on every filter change and fires `dom-updated` on the
        // element it replaced, which drops the markers — and the open bubble's anchor — with it.
        document.addEventListener('dom-updated', () => {
            hide();
            window.requestAnimationFrame(attach);
        });

        /*
         * The marker sits inside the filter's own label, and the FactFinder component listens for
         * the selection above it — early enough that a `stopPropagation()` on the marker itself
         * came too late: clicking the icon selected the filter and the re-render then tore the
         * bubble down again. Intercepting on `document` in the capture phase is the one place that
         * reliably runs first. `mousedown`/`pointerdown` are held back for the same reason: some
         * builds act on the press rather than the click.
         */
        const intercept = (event) => {
            const marker = event.target instanceof Element
                ? event.target.closest('.wh1-asn-tooltip')
                : null;
            if (!marker) {
                if (event.type === 'click') {
                    hide(); // a click anywhere else dismisses it
                }
                return null;
            }
            /*
             * Only the propagation is stopped unconditionally — that is what keeps FactFinder from
             * selecting the filter under the marker. `preventDefault()` is held to mouse input:
             * cancelling the default on a touch press stops the browser synthesising the click the
             * tap handling depends on.
             */
            if (!('pointerType' in event) || event.pointerType === 'mouse') {
                event.preventDefault();
            }
            event.stopPropagation();
            event.stopImmediatePropagation();
            return marker;
        };

        ['pointerdown', 'mousedown', 'mouseup'].forEach((type) => {
            document.addEventListener(type, intercept, true);
        });

        document.addEventListener('click', (event) => {
            const marker = intercept(event);
            if (!marker) {
                return;
            }
            if (openMarker === marker) {
                hide();
            } else {
                show(marker);
            }
        }, true);

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                hide();
            }
        });
        window.addEventListener('scroll', place, { passive: true });
        window.addEventListener('resize', place);
    }

    function initFactFinderRatingSliders() {
        /*
         * #70172 — the three rating scales arrive collapsed.
         *
         * Preis and Skilänge are opened by the bundle; Terrain, Schwung and Speed are not, so on
         * the Stöckli brand page the three groups the ticket is about show nothing but a caption
         * until they are clicked. Production has them open.
         *
         * The group is opened through the bundle's own caption, never by writing `opened` by hand:
         * the component keeps its state internally and a hand-written open leaves a panel that
         * cannot be closed again — the same trap #70138 hit from the other direction. Each group is
         * opened at most once, so closing one by hand stays closed; a re-render from FactFinder
         * replaces the element and the fresh one opens again.
         *
         * Desktop only. Below 992px the same markup is the filter drawer, which #70138 deliberately
         * hands over with every group collapsed.
         */
        const RATING_GROUPS = ['Terrain', 'Schwung', 'Swing', 'Speed'];
        const desktop = window.matchMedia('(min-width: 992px)');

        const openRatingGroups = () => {
            if (!desktop.matches) {
                return;
            }

            document.querySelectorAll('.kussin-factfinder-asn ff-asn-group-slider').forEach((group) => {
                if (RATING_GROUPS.indexOf(group.getAttribute('for-group')) === -1) {
                    return;
                }
                if (group.dataset.kussinRatingOpened === '1') {
                    return;
                }

                const wrapper = group.querySelector('.ffw-wrapper');
                const caption = group.querySelector('[data-container="groupCaption"] > *');
                if (!wrapper || !caption || wrapper.hasAttribute('opened')) {
                    return;
                }

                group.dataset.kussinRatingOpened = '1';
                caption.click();
            });
        };

        window.requestAnimationFrame(openRatingGroups);
        document.addEventListener('dom-updated', () => window.requestAnimationFrame(openRatingGroups));
    }

    function initFactFinderSliderLabels() {
        /*
         * #70058 — "1 Piste" .. "10 Piste".
         *
         * FactFinder ships a `unit` with every range facet and the bundle prints it after both
         * bounds. For Preis and the lengths that reads correctly (2,00 € .. 1.799,00 €), but the
         * three rating scales carry a word describing one end of the scale rather than a unit, so
         * both fields ended up with the same suffix: "1 Piste" .. "10 Piste", "1 Lang" .. "10 Lang",
         * "1 Schnell" .. "9 Schnell". The suffix says nothing at either end and reads as a fault.
         *
         * The unit lives in the FactFinder index configuration, which is not editable from the
         * shop, so the value is corrected on the way out: a suffix that is an actual unit of
         * measurement is kept, anything else is dropped and the bare number is shown under the
         * group's own caption. Only the display changes — the bundle parses these fields with
         * parseFloat, which reads "10" exactly as it read "10 Piste".
         */
        const REAL_UNIT = /^(cm|mm|m|km|g|kg|t|l|ml|€|%|"|inch|in)$/i;

        const clean = () => {
            document.querySelectorAll('ff-asn-group-slider input').forEach((input) => {
                const match = /^\s*([\d.,]+)\s+(\S+)\s*$/.exec(input.value || '');
                if (!match || REAL_UNIT.test(match[2])) {
                    return;
                }
                input.value = match[1];
            });
        };

        clean();

        document.addEventListener('dom-updated', () => window.requestAnimationFrame(clean));
        document.addEventListener('input', (event) => {
            if (event.target instanceof Element && event.target.closest('ff-asn-group-slider')) {
                window.requestAnimationFrame(clean);
            }
        }, true);

        // The bundle rewrites both fields on every frame of a drag, so re-clean until the pointer
        // is released rather than once at the end.
        document.addEventListener('pointerdown', (event) => {
            if (!(event.target instanceof Element) || !event.target.closest('ff-asn-group-slider')) {
                return;
            }
            let dragging = true;
            const tick = () => {
                if (!dragging) {
                    return;
                }
                clean();
                window.requestAnimationFrame(tick);
            };
            const stop = () => {
                dragging = false;
                clean();
                document.removeEventListener('pointerup', stop);
                document.removeEventListener('pointercancel', stop);
            };
            document.addEventListener('pointerup', stop);
            document.addEventListener('pointercancel', stop);
            window.requestAnimationFrame(tick);
        }, true);
    }

    /**
     * #70055 — keep the category header in step with the left-hand category facet.
     *
     * The category navigation on the left is FactFinder's `CategoryPathROOT` TREE facet. Clicking it
     * is a pure XHR: the record list and the result count update, but nothing re-runs
     * `oView.getTitle()` or re-renders the hero, so the page kept showing the category you arrived
     * on while listing a different one's products.
     *
     * OXID 6 solved this with `wmdkReloadCategoryHeader()`, which asks the shop for the category by
     * name and swaps the heading and the images. That AJAX endpoint was already ported —
     * `Kussin\Wh1CoreModifications\Component\Widget\ArticleListAjax::getCategoryByName()` — and
     * answers correctly; only this client half was missing.
     */
    function initFactFinderCategoryHeader() {
        /*
         * #70134 — a brand landing is not a category listing.
         *
         * This routine keeps the headline and the breadcrumb in step with the selected
         * CategoryPathROOT facet (#70054 / #70055). On a manufacturer page FactFinder pre-selects
         * that facet too, because every product of a brand shares a category — so on /nidecker/ the
         * server sent "Home / Marken / NIDECKER" and the title NIDECKER, and a second later the JS
         * rewrote them to "Home / Snow / Snowboard" and "Snowboard". Production never touches
         * either on a brand page, and the effect is invisible to curl: only a rendered browser
         * shows it.
         */
        if (document.body.classList.contains('cl-manufacturerlist')
            || document.body.classList.contains('cl-vendorlist')) {
            return;
        }

        const heroTitle = document.querySelector('.wh1-cat-hero-title');
        const listTitle = document.querySelector('.kussin-factfinder-list-title');
        if (!heroTitle && !listTitle) {
            return;
        }

        // `textContent`, not `innerText`: the hero title is uppercased by CSS, so innerText would
        // report "KIDS SKATES" and never match the category name coming back from the shop.
        const currentTitle = () => (heroTitle || listTitle).textContent.trim();

        const deepestSelectedCategory = () => {
            const selected = document.querySelectorAll(
                'ff-asn-group[for-group="CategoryPathROOT"] .ffw-asn-selected'
            );
            return selected.length ? selected[selected.length - 1].textContent.trim() : '';
        };

        let inFlight = false;

        const sync = () => {
            const wanted = deepestSelectedCategory();
            if (!wanted || inFlight || wanted === currentTitle()) {
                return;
            }
            inFlight = true;

            // The endpoint expects "&" pre-encoded as `wmdk_and` — it reverses that server-side.
            // Category names such as "Sohlen & Zubehör" otherwise truncate at the ampersand.
            const catname = encodeURIComponent(wanted.replace(/&/g, 'wmdk_and'));

            // Must be the shop's absolute self link (emitted in base.html.twig): a relative
            // "index.php?…" resolves against the current document, and every listing runs on a SEO
            // path with a trailing slash, so it would request
            // "/skate/inliner/kids-skates/index.php?…" and 404.
            const base = window.WH1_SELF_LINK || 'index.php?';
            const separator = /[?&]$/.test(base) ? '' : (base.includes('?') ? '&' : '?');

            fetch(`${base}${separator}cl=articlelistajax&fnc=getCategoryByName&catname=${catname}`, {
                credentials: 'same-origin',
            })
                .then((response) => (response.ok ? response.json() : null))
                .then((data) => {
                    if (!data || !data.success || !data.title) {
                        return;
                    }

                    if (heroTitle) heroTitle.textContent = data.title;
                    if (listTitle) listTitle.textContent = data.title;

                    // #70054 — rewrite the whole trail, not just its last entry.
                    //
                    // Renaming the active crumb kept the crumb COUNT of the page that was loaded,
                    // so drilling from /skate/inliner/ into "Kids Skates" produced
                    // "Skate > Kids Skates": the level in between disappeared and the trail claimed
                    // a parent that is not the category's parent. The endpoint now sends the real
                    // ancestry, so the list is rebuilt from it and stays correct at any depth.
                    //
                    // The first <li> is the home icon and is left alone; everything after it is
                    // replaced. Anchors are built with textContent, never innerHTML — the titles
                    // are shop data and may contain characters that would need escaping.
                    const crumbList = document.querySelector('.breadcrumb-wrapper .breadcrumb');
                    const trail = Array.isArray(data.breadcrumb) ? data.breadcrumb : [];
                    if (crumbList && trail.length) {
                        const items = crumbList.querySelectorAll('li');
                        for (let i = items.length - 1; i >= 1; i -= 1) {
                            items[i].remove();
                        }
                        trail.forEach((crumb, index) => {
                            const item = document.createElement('li');
                            item.className = 'breadcrumb-item';
                            if (index === trail.length - 1) {
                                item.classList.add('active');
                                item.textContent = crumb.title;
                            } else {
                                const link = document.createElement('a');
                                link.className = 'breadcrumb-link';
                                link.href = crumb.link || '#';
                                link.title = crumb.title;
                                link.textContent = crumb.title;
                                item.appendChild(link);
                            }
                            crumbList.appendChild(item);
                        });
                    }

                    const img = document.querySelector('.wh1-cat-hero-img');
                    if (img && data.image) {
                        img.src = data.image;
                        img.alt = img.alt.replace(/[“"][^”"]*[”"]/, `“${data.title}”`);
                    }

                    // The mobile variant is an optional <source> — add, update or drop it so the
                    // picture never keeps the previous category's mobile image.
                    const picture = document.querySelector('.wh1-cat-hero-picture');
                    if (picture) {
                        let source = picture.querySelector('source');
                        if (data.mobileimage) {
                            if (!source) {
                                source = document.createElement('source');
                                source.media = '(max-width: 991.98px)';
                                picture.insertBefore(source, picture.firstElementChild);
                            }
                            source.srcset = data.mobileimage;
                        } else if (source) {
                            source.remove();
                        }
                    }
                })
                .catch(() => {})
                .finally(() => {
                    inFlight = false;
                });
        };

        document.addEventListener('dom-updated', () => window.requestAnimationFrame(sync));
    }

    /**
     * #70059 — tidy range/slider entries in the "selected filters" cloud.
     *
     * FactFinder renders a slider selection as a bracketed float pair, e.g. `[80.00 - 175.00]`,
     * with no unit — so a length filter read "Snowboard Länge [80.00 - 175.00]" instead of
     * "80 - 175 cm". The OXID 6 shop cleaned this up in `wmdkCleanAsnFilterCloud()`
     * (`assets/js/factfinder.js`), which is dead code here and still targets the old
     * `.fc-filter-item` / `.filter-item-name` markup; the OXID 7 template emits
     * `.kussin-factfinder-filtercloud-*`.
     *
     * The unit map is carried over unchanged from that function, both languages.
     */
    const FF_FILTER_UNITS = {
        'Preis': '€',
        'Price': '€',
        'Wakeboard Länge': 'cm',
        'Wakeboard length': 'cm',
        'Snowboard Länge': 'cm',
        'Snowboard length': 'cm',
        'Snowboard Länge (Set)': 'cm',
        'Snowboard Set length': 'cm',
        'Ski Länge': 'cm',
        'Ski length': 'cm',
        'Stock Länge': 'cm',
        'Ski pole length': 'cm',
        'SUP Fahrergewicht': 'kg',
        'SUP rider weight': 'kg',
        'SUP Volumen': 'Liter',
        'SUP Volume': 'Liter',
    };

    function initFactFinderFilterCloud() {
        const clean = () => {
            document.querySelectorAll('.kussin-factfinder-filtercloud-item').forEach((item) => {
                const nameEl = item.querySelector('.kussin-factfinder-filtercloud-name');
                const groupEl = item.querySelector('.kussin-factfinder-filtercloud-group');
                if (!nameEl || !groupEl) {
                    return;
                }

                const original = nameEl.textContent;
                // Only range selections carry brackets; leave ordinary values alone.
                if (!original.includes('[')) {
                    return;
                }

                const unit = FF_FILTER_UNITS[groupEl.textContent.trim()];
                const cleaned = original
                    .replace(/\[/g, '')
                    .replace(/\]/g, '')
                    .replace(/\.00/g, '')
                    .trim();

                nameEl.textContent = unit ? `${cleaned} ${unit}` : cleaned;
            });
        };

        clean();
        // The cloud is re-rendered on every filter change.
        document.addEventListener('dom-updated', () => window.requestAnimationFrame(clean));
    }

    /**
     * #70025 — reveal the "back to top" control once the page has scrolled.
     *
     * Production shows it permanently via an inline `onclick`; here it stays hidden until there is
     * something to scroll back from, and the scroll itself respects prefers-reduced-motion.
     */
    /**
     * #70017 — subcategory tiles whose thumbnail was never generated.
     *
     * The tiles ask for the 1140x250 derivative, which exists for almost every category. Where it
     * does not, the CDN does NOT answer 404 — it returns HTTP 200 with a 200x200 placeholder (1kB
     * WebP). So `onerror` never fires, nothing falls back, and the tile shows a blank square:
     * "Accessoires" on /en/fashion-more/, against a real image on production.
     *
     * The stub is recognisable by its shape: a real category thumb is wide (1140x244, 261x56), the
     * stub is square and small. When one is detected the next candidate is tried — the 261x126
     * derivative, then the `wmdkresized_` variant of it, which is the file production serves and the
     * only one that exists for this category — and finally the theme's own placeholder so a tile is
     * never left showing the CDN's stub.
     *
     * Each step is recorded on the element, so a candidate that is itself missing cannot loop.
     */
    /**
     * #70072 — CMS accordions must close the panel they replace.
     *
     * The migrated records carry `data-bs-parent="#accordion"` on the trigger, which is where
     * Bootstrap 3 wanted it. Bootstrap 5 reads that attribute from the COLLAPSE element instead, so
     * it was ignored and every panel opened independently: clicking three headings left three panels
     * expanded. The attribute is copied to the panel each trigger controls, which restores the
     * one-open-at-a-time behaviour the content was written for.
     *
     * Done here rather than in the record: the markup is editor-maintained CMS content, and every
     * page carrying this legacy accordion is fixed at once. Bootstrap reads the attribute when it
     * initialises the collapse on first use, so setting it up front is enough.
     */
    function initCmsAccordions() {
        /*
         * Scope: migrated CMS *page* records only.
         *
         * This used to walk the whole document, which meant any widget anywhere — a product page's
         * injected content included — could be rewritten by a fix written for one showroom record.
         * Nothing outside a CMS record carries Bootstrap 3 collapse attributes, so the upgrade
         * belongs inside `.cmsContent` and nowhere else; the injector's own blocks
         * (`.cnc-contentinjector`, used on product pages) are deliberately left alone.
         */
        const roots = document.querySelectorAll('.cmsContent');
        if (!roots.length) {
            return;
        }
        const findAll = (selector) => {
            const out = [];
            roots.forEach((root) => out.push(...root.querySelectorAll(selector)));
            return out;
        };

        /*
         * #70072 — collapse controls still written for Bootstrap 3.
         *
         * The records were authored against Bootstrap 3, whose data API used `data-toggle` and
         * `data-target`; Bootstrap 5 only listens for the `data-bs-` prefixed pair. Most of the
         * showroom accordion was converted at some point, but the newsletter signup button was not:
         *
         *     <button class="btn btn-primary showmore" data-toggle="collapse"
         *             data-target="#newsletteranmeldung">
         *
         * so clicking "Ja, für den Showroom Newsletter anmelden" did nothing at all and the signup
         * form could not be reached — reported as "the newsletter signup is not working". The
         * prefixed attributes are added where they are missing; Bootstrap's data API is delegated,
         * so a control upgraded after load still works on the first click.
         */
        findAll('[data-toggle="collapse"]:not([data-bs-toggle])').forEach((toggle) => {
            toggle.setAttribute('data-bs-toggle', 'collapse');
            const target = toggle.getAttribute('data-target');
            if (target && !toggle.hasAttribute('data-bs-target')) {
                toggle.setAttribute('data-bs-target', target);
            }
            const parent = toggle.getAttribute('data-parent');
            if (parent && !toggle.hasAttribute('data-bs-parent')) {
                toggle.setAttribute('data-bs-parent', parent);
            }
        });

        findAll('[data-bs-toggle="collapse"][data-bs-parent]').forEach((toggle) => {
            const parent = toggle.getAttribute('data-bs-parent');
            const selector = toggle.getAttribute('data-bs-target') || toggle.getAttribute('href');
            if (!parent || !selector || selector.charAt(0) !== '#') {
                return;
            }
            let panel = null;
            try {
                panel = document.querySelector(selector);
            } catch (e) {
                return; // a href that is not a usable selector
            }
            if (panel && !panel.hasAttribute('data-bs-parent')) {
                panel.setAttribute('data-bs-parent', parent);
            }
        });
    }

    /*
     * #70043 — behaviour the migrated CMS records used to get from production's jQuery.
     *
     * Several records were copied out of OXID 6 together with
     * `<script src="https://www.warehouse-one.de/out/wh1-flow/src/js/libs/jquery.min.js">` and an
     * inline `$(document).ready(...)` block. Loading a script from the production shop to make a
     * test shop work is the defect the ticket is about, so the includes are being removed from the
     * content. Most of what those blocks did is dead — they address Bootstrap 3 classes
     * (`col-xs-12`, `hidden-xs`) and elements that no longer exist, and the pages two of them
     * belonged to (`.gotonewsletter`, `.beratungstermin`, the 2020 homepage slider) no longer carry
     * those hooks at all. What is still live is reproduced here, in the theme, without jQuery.
     *
     * Each function below is a no-op unless every element it needs is on the page, so the Sale
     * behaviour costs a failed `querySelector` on every other listing.
     */

    function initCmsSaleHeadlineLanding() {
        /*
         * The Sale tiles link to filtered sub-landings, e.g.
         * `/sale/?filterCategoryPathROOT=Snow&headline=Snow Sale`. The heading itself is already
         * handled server side (Wh1CoreModifications ArticleListController::getTitle() reads the
         * `headline` parameter), but hiding the tile grid and the Sale-of-the-Day teaser — so the
         * sub-landing shows the filtered listing and not the whole Sale front page again — was
         * only ever done by the inline script.
         */
        const params = new URLSearchParams(window.location.search);
        if (!params.get('headline')) {
            return;
        }

        document
            .querySelectorAll('.categoryDescription .masonry-wrapper, .dd-shortcode-wmdksaleoftheday')
            .forEach((element) => {
                element.style.display = 'none';
            });
    }

    function initCmsEmptyCategoryDescription() {
        /*
         * #70043 — drop the category-description band when its CMS content is all hidden.
         *
         * `showCategoryLongDescription` is decided server side on the raw field, which on /sale/ is
         * a record whose every cell carries `hidden-xs hidden-sm hidden-md hidden-lg`. The band then
         * renders around nothing and its 40px padding shows as an empty white strip under the
         * heading. Only the browser knows what those classes do at this width, so the measurement
         * has to happen here.
         *
         * Measured with the band shown, otherwise hiding it once would make every later measurement
         * report zero and the band could never come back.
         */
        const description = document.querySelector('#catLongDescLocator');
        const band = description ? description.closest('.wh1-cat-description') : null;

        if (!band) {
            return;
        }

        const sync = () => {
            band.classList.remove('wh1-cat-description--empty');

            if (description.getBoundingClientRect().height === 0) {
                band.classList.add('wh1-cat-description--empty');
            }
        };

        sync();
        // Images inside the description have no height until they load; re-measure once they do.
        window.addEventListener('load', sync, { once: true });
    }

    function initSaleCountdown() {
        /*
         * #70043 — count every `[data-wh1-countdown-until]` element down to the moment named in its
         * attribute.
         *
         * The Sale banner needs the running clock production shows beside it. This is deliberately
         * generic: the element says when it ends and this only formats what is left, so no page is
         * recognised here and no node is moved out of a CMS widget to borrow its timer, which is
         * what production's script did.
         */
        const counters = document.querySelectorAll('[data-wh1-countdown-until]');

        if (!counters.length) {
            return;
        }

        const pad = (value) => String(value).padStart(2, '0');

        const tick = () => {
            counters.forEach((counter) => {
                // `Date.parse` needs slashes to read the value as local time in every browser.
                const until = Date.parse(counter.getAttribute('data-wh1-countdown-until').replace(/-/g, '/'));

                if (Number.isNaN(until)) {
                    return;
                }

                const left = Math.max(0, until - Date.now());

                counter.textContent = [
                    Math.floor(left / 3600000),
                    Math.floor((left % 3600000) / 60000),
                    Math.floor((left % 60000) / 1000),
                ].map(pad).join(':');
            });
        };

        tick();
        window.setInterval(tick, 1000);
    }

    function initCmsNewsletterReveal() {
        /*
         * "Ja, für den Showroom Newsletter anmelden" on the showroom thank-you page.
         *
         * The record hides its own signup row with a stylesheet it carries inline
         * (`div.slom_newsletter > div:nth-child(2) { display: none; }`) and production revealed it
         * with jQuery's `.toggle()`. Bootstrap cannot take over here the way it did for the store
         * page in #70072: the anchor's collapse target `#newsletteranmeldung` is a different,
         * empty element, and the row that holds the form sits outside it. Without this the button
         * is inert and the form cannot be reached at all.
         *
         * Scoped to the anchor variant on purpose. The store page's control is a `button.showmore`
         * driven by Bootstrap's collapse, and must not be toggled twice.
         */
        const panels = document.querySelectorAll('.slom_newsletter > div:nth-child(2)');
        if (!panels.length) {
            return;
        }

        document.addEventListener('click', (event) => {
            const trigger = event.target.closest('a.showmore');
            if (!trigger) {
                return;
            }

            event.preventDefault();

            panels.forEach((panel) => {
                const hidden = window.getComputedStyle(panel).display === 'none';
                // The siblings are Bootstrap rows, so restore the row's own display, not `block`.
                panel.style.display = hidden ? 'flex' : '';
                if (hidden) {
                    panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            });
        });
    }

    function initSubcatTileFallback() {
        const images = document.querySelectorAll('.subcat-tile-img');
        if (!images.length) {
            return;
        }

        const looksUnavailable = (img) =>
            img.naturalWidth > 0 && img.naturalWidth <= 200 && img.naturalWidth === img.naturalHeight;

        const nextSource = (img) => {
            const src = img.currentSrc || img.src || '';
            const step = Number(img.getAttribute('data-tile-fallback') || 0);
            const file = src.split('/').pop();
            if (step === 0 && src.includes('/1140_250_90/')) {
                return src.replace('/1140_250_90/', '/261_126_90/');
            }
            if (step <= 1 && src.includes('/261_126_90/') && !file.startsWith('wmdkresized_')) {
                return src.replace(file, 'wmdkresized_' + file);
            }
            return null;
        };

        images.forEach((img) => {
            const check = () => {
                if (!looksUnavailable(img)) {
                    return;
                }
                const next = nextSource(img);
                if (!next) {
                    return;
                }
                img.setAttribute('data-tile-fallback', String(Number(img.getAttribute('data-tile-fallback') || 0) + 1));
                img.src = next;
            };

            img.addEventListener('load', check);
            if (img.complete) {
                check();
            }
        });
    }

    function initBackToTop() {
        const button = document.getElementById('wh1-back-to-top');
        if (!button) {
            return;
        }

        button.hidden = false;
        const threshold = () => window.innerHeight * 0.75;

        const sync = () => {
            button.classList.toggle('is-visible', window.scrollY > threshold());
        };

        button.addEventListener('click', () => {
            const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
        });

        window.addEventListener('scroll', sync, { passive: true });
        window.addEventListener('resize', sync, { passive: true });
        sync();
    }

    function initMobileHeader() {
        const header = document.querySelector('[data-kussin-mobile-header]');
        const headerInner = header
            ? header.querySelector(':scope > [data-kussin-mobile-header-inner]')
            : null;

        if (!header || !headerInner || header.dataset.kussinMobileHeaderBound === '1') {
            return;
        }

        header.dataset.kussinMobileHeaderBound = '1';

        const mobileViewport = window.matchMedia('(max-width: 991.98px)');
        const compactTransitionDuration = 200;
        let expandedHeight = 0;
        let measuredViewportWidth = 0;
        let frameRequested = false;
        let expandedMeasurementTimer = 0;
        let expandedMeasurementSuspended = false;

        const resetMobileLayout = () => {
            window.clearTimeout(expandedMeasurementTimer);
            expandedMeasurementSuspended = false;
            header.classList.remove('is-mobile-compact');
            header.removeAttribute('data-kussin-mobile-header-fixed');
            header.style.removeProperty('height');
            expandedHeight = 0;
            measuredViewportWidth = 0;
        };

        const measureExpandedLayout = () => {
            if (
                !mobileViewport.matches
                || header.classList.contains('is-mobile-compact')
            ) {
                return;
            }

            const expandedBounds = headerInner.getBoundingClientRect();

            expandedHeight = expandedBounds.height;
            measuredViewportWidth = window.innerWidth;
            header.style.height = `${expandedBounds.height}px`;
        };

        const scheduleExpandedMeasurement = () => {
            window.clearTimeout(expandedMeasurementTimer);
            expandedMeasurementSuspended = true;
            expandedMeasurementTimer = window.setTimeout(() => {
                expandedMeasurementSuspended = false;
                measureExpandedLayout();
            }, compactTransitionDuration);
        };

        const updateState = () => {
            const isCompact = header.classList.contains('is-mobile-compact');

            if (!mobileViewport.matches) {
                resetMobileLayout();
                frameRequested = false;
                return;
            }

            if (
                expandedHeight === 0
                || (
                    measuredViewportWidth !== window.innerWidth
                    && !isCompact
                )
            ) {
                measureExpandedLayout();
            }

            const headerDocumentTop = header.getBoundingClientRect().top + window.scrollY;
            const shouldCompact = window.scrollY > headerDocumentTop;

            if (shouldCompact && !isCompact) {
                window.clearTimeout(expandedMeasurementTimer);
                header.dataset.kussinMobileHeaderFixed = '1';
                header.classList.add('is-mobile-compact');
            } else if (!shouldCompact && isCompact) {
                header.classList.remove('is-mobile-compact');
                header.removeAttribute('data-kussin-mobile-header-fixed');
                scheduleExpandedMeasurement();
            }

            frameRequested = false;
        };

        const requestUpdate = () => {
            if (frameRequested) {
                return;
            }

            frameRequested = true;
            window.requestAnimationFrame(updateState);
        };

        const expandedLayoutObserver = typeof ResizeObserver === 'function'
            ? new ResizeObserver(() => {
                if (
                    mobileViewport.matches
                    && !header.classList.contains('is-mobile-compact')
                    && !expandedMeasurementSuspended
                    && measuredViewportWidth === window.innerWidth
                ) {
                    measureExpandedLayout();
                }
            })
            : null;

        if (expandedLayoutObserver) {
            expandedLayoutObserver.observe(headerInner);
        }

        window.addEventListener('scroll', requestUpdate, { passive: true });
        window.addEventListener('resize', requestUpdate, { passive: true });
        if (typeof mobileViewport.addEventListener === 'function') {
            mobileViewport.addEventListener('change', requestUpdate);
        } else {
            mobileViewport.addListener(requestUpdate);
        }

        updateState();
    }

    function initProductVariantOptions() {
        if (document.documentElement.dataset.kussinVariantOptionsBound === '1') {
            return;
        }

        document.documentElement.dataset.kussinVariantOptionsBound = '1';
        document.addEventListener('click', (event) => {
            const option = event.target.closest
                ? event.target.closest('[data-kussin-variant-value]')
                : null;
            const group = option ? option.closest('.kussin-product-variant-group') : null;
            const select = group ? group.querySelector('.kussin-product-variant-select') : null;

            if (!option || option.disabled || !select) {
                return;
            }

            select.value = option.dataset.kussinVariantValue;
            group.querySelectorAll('[data-kussin-variant-value]').forEach((item) => {
                const active = item === option;
                item.classList.toggle('is-active', active);
                item.setAttribute('aria-pressed', active ? 'true' : 'false');
            });
            select.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    function initDefectiveCopiesAgreement() {
        if (document.documentElement.dataset.kussinDefectiveCopiesBound === '1') {
            return;
        }
        document.documentElement.dataset.kussinDefectiveCopiesBound = '1';

        const sync = () => {
            document.querySelectorAll('.js-oxProductForm').forEach((form) => {
                const checkbox = form.querySelector('[data-kussin-defective-copies] input[type="checkbox"]');
                const button = form.querySelector('#toBasket');
                button?.classList.toggle('kussin-agreement-required', Boolean(checkbox && !checkbox.checked));
            });
        };

        // Keep agreement state separate from the variant selector's not-saleable state.
        const guard = (event) => {
            const button = event.type === 'click' ? event.target.closest?.('#toBasket') : null;
            const form = button?.form || (event.type === 'submit' ? event.target : null);
            const checkbox = form?.querySelector('[data-kussin-defective-copies] input[type="checkbox"]');
            if (!checkbox || checkbox.checked) {
                return;
            }
            event.preventDefault();
            event.stopImmediatePropagation();
            checkbox.scrollIntoView({ block: 'center', behavior: 'smooth' });
            checkbox.reportValidity();
        };

        document.addEventListener('click', guard, true);
        document.addEventListener('submit', guard, true);
        document.addEventListener('change', (event) => {
            if (event.target.id === 'kussinDefectiveCopiesAgreement') {
                sync();
            }
        });
        document.addEventListener('dom-updated', sync);
        document.addEventListener('DOMContentLoaded', sync);
        window.addEventListener('pageshow', sync);
        sync();
    }

    /* #70776 — Cloudflare serves product pages from its cache, so the stoken in their basket forms
       belongs to whoever the page was rendered for. A visitor with a session of their own posted
       that foreign token and the first "add to cart" failed with the CSRF message. Fetch the
       visitor's own token just before the form goes out. It runs after the capture-phase guards, so
       a submit they cancelled is left alone, and a failed fetch still submits the form as before. */
    function initFreshBasketToken() {
        if (document.documentElement.dataset.kussinFreshTokenBound === '1') {
            return;
        }
        document.documentElement.dataset.kussinFreshTokenBound = '1';

        document.addEventListener('submit', (event) => {
            const form = event.target;
            const fnc = form?.querySelector?.('input[name="fnc"]');

            if (event.defaultPrevented || !fnc || fnc.value !== 'tobasket' || form.dataset.kussinTokenFresh === '1') {
                return;
            }

            event.preventDefault();
            const submitter = event.submitter;

            fetch(form.action || window.location.href, {
                method: 'POST',
                credentials: 'same-origin',
                cache: 'no-store',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                body: new URLSearchParams({ cl: 'kussin_session_token' }),
            })
                .then((response) => (response.ok ? response.json() : null))
                .then((data) => {
                    if (data && data.stoken) {
                        form.querySelectorAll('input[name="stoken"]').forEach((input) => {
                            input.value = data.stoken;
                        });
                    }
                })
                .catch(() => {})
                .finally(() => {
                    form.dataset.kussinTokenFresh = '1';
                    if (form.requestSubmit) {
                        form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
                    } else {
                        form.submit();
                    }
                    delete form.dataset.kussinTokenFresh;
                });
        });
    }

    function initProductStickyCart() {
        if (document.documentElement.dataset.kussinStickyCartBound === '1') {
            return;
        }

        document.documentElement.dataset.kussinStickyCartBound = '1';

        const root = document.documentElement;
        const mobileViewport = window.matchMedia('(max-width: 991.98px)');
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        let anchor = null;
        let control = null;
        let discountLabel = null;
        let discountOrigin = null;
        let discountNextSibling = null;
        let observer = null;
        let sizeObserver = null;
        let refreshRequested = false;

        const restoreDiscountLabel = () => {
            if (!discountLabel) {
                return;
            }

            discountLabel.classList.remove('kussin-sticky-cart-discount');

            if (
                !discountLabel.isConnected
                || !discountOrigin?.isConnected
                || discountLabel.parentNode === discountOrigin
            ) {
                return;
            }

            if (discountNextSibling?.parentNode === discountOrigin) {
                discountOrigin.insertBefore(discountLabel, discountNextSibling);
            } else {
                discountOrigin.append(discountLabel);
            }
        };

        const mountDiscountLabel = () => {
            if (!discountLabel || !control) {
                return;
            }

            discountLabel.classList.add('kussin-sticky-cart-discount');

            if (discountLabel.parentNode === control) {
                return;
            }

            control.insertBefore(discountLabel, control.firstChild);
        };

        /*
         * #70201 — how much vertical room the bar really takes at the bottom of the screen.
         *
         * The bar's own border box is not the whole of it: the discount label is mounted into it
         * `position: absolute; top: -25px; height: 45px` (components/product-detail/sticky-cart.less),
         * so on a discounted article it hangs 25px above the bar and outside every measurement of
         * it. `--kussin-sticky-cart-height` stays the bar's height, because that is what the page
         * reserves as bottom padding; the clearance is what anything standing on top of the bar —
         * the back-to-top control — has to clear.
         */
        const publishMetrics = () => {
            const controlRect = control.getBoundingClientRect();
            let top = controlRect.top;

            if (discountLabel?.isConnected && discountLabel.parentNode === control) {
                const labelRect = discountLabel.getBoundingClientRect();

                if (labelRect.height > 0) {
                    top = Math.min(top, labelRect.top);
                }
            }

            root.style.setProperty('--kussin-sticky-cart-height', `${controlRect.height}px`);
            root.style.setProperty(
                '--kussin-sticky-cart-clearance',
                `${Math.max(controlRect.height, controlRect.bottom - top)}px`,
            );
        };

        const clearMetrics = () => {
            root.style.removeProperty('--kussin-sticky-cart-height');
            root.style.removeProperty('--kussin-sticky-cart-clearance');
        };

        /*
         * #70201 — the measurement has to survive the bar changing size after it was taken.
         *
         * `publishMetrics()` runs one frame after the bar goes sticky. That is the right answer
         * for the layout of that moment and the wrong one for everything that reflows afterwards:
         * a web font arriving late, a price that rewraps, a savings label that grows a line.
         * None of those fires `resize` or `dom-updated`, so the clearance would keep the value it
         * had while the bar underneath it grew — and the back-to-top control would settle back
         * down onto the label. Watching both boxes keeps the two in step while the bar is up.
         */
        const watchMetrics = () => {
            if (typeof ResizeObserver !== 'function') {
                return;
            }

            sizeObserver?.disconnect();
            sizeObserver = new ResizeObserver(() => {
                if (anchor?.classList.contains('is-sticky')) {
                    publishMetrics();
                }
            });

            if (control) {
                sizeObserver.observe(control);
            }

            if (discountLabel?.isConnected) {
                sizeObserver.observe(discountLabel);
            }
        };

        const setSticky = (active) => {
            if (!anchor || !control) {
                restoreDiscountLabel();
                root.classList.remove('kussin-sticky-cart-active');
                sizeObserver?.disconnect();
                clearMetrics();
                return;
            }

            if (!active) {
                restoreDiscountLabel();
                anchor.classList.remove('is-sticky');
                anchor.style.removeProperty('--kussin-sticky-cart-anchor-height');
                root.classList.remove('kussin-sticky-cart-active');
                sizeObserver?.disconnect();
                clearMetrics();
                return;
            }

            anchor.style.setProperty(
                '--kussin-sticky-cart-anchor-height',
                `${control.getBoundingClientRect().height}px`,
            );
            mountDiscountLabel();
            anchor.classList.add('is-sticky');
            root.classList.add('kussin-sticky-cart-active');

            window.requestAnimationFrame(() => {
                if (anchor?.classList.contains('is-sticky')) {
                    publishMetrics();
                    watchMetrics();
                }
            });
        };

        const updateState = () => {
            if (!anchor || !control || !mobileViewport.matches) {
                setSticky(false);
                return;
            }

            setSticky(anchor.getBoundingClientRect().bottom <= 0);
        };

        const bindCurrentCart = () => {
            refreshRequested = false;

            const nextAnchor = document.querySelector('[data-kussin-sticky-cart]');
            const nextControl = nextAnchor
                ? nextAnchor.querySelector('.tobasketFunction, .tobasket-function')
                : null;
            const nextButton = nextControl ? nextControl.querySelector('#toBasket') : null;
            const nextPurchase = nextAnchor ? nextAnchor.closest('.kussin-product-purchase') : null;
            const nextDiscountLabel = nextPurchase
                ? nextPurchase.querySelector('.wmdk-discount-rate')
                : null;

            if (
                nextAnchor === anchor
                && nextControl === control
                && nextDiscountLabel === discountLabel
            ) {
                updateState();
                return;
            }

            if (observer) {
                observer.disconnect();
                observer = null;
            }

            if (sizeObserver) {
                sizeObserver.disconnect();
                sizeObserver = null;
            }

            setSticky(false);
            anchor = nextButton ? nextAnchor : null;
            control = nextButton ? nextControl : null;
            discountLabel = nextButton ? nextDiscountLabel : null;
            discountOrigin = discountLabel ? discountLabel.parentNode : null;
            discountNextSibling = discountLabel ? discountLabel.nextSibling : null;

            if (!anchor || !control) {
                return;
            }

            observer = new IntersectionObserver((entries) => {
                const entry = entries[0];

                setSticky(
                    mobileViewport.matches
                    && !entry.isIntersecting
                    && entry.boundingClientRect.bottom <= 0,
                );
            });
            observer.observe(anchor);
            updateState();
        };

        const requestRefresh = () => {
            if (refreshRequested) {
                return;
            }

            refreshRequested = true;
            window.requestAnimationFrame(bindCurrentCart);
        };

        document.addEventListener('dom-updated', requestRefresh);
        document.addEventListener('click', (event) => {
            const button = event.target.closest ? event.target.closest('#toBasket.not-saleable') : null;

            if (!button || !anchor?.classList.contains('is-sticky') || !anchor.contains(button)) {
                return;
            }

            const variantGroup = document.querySelector('.kussin-product-variant-group');
            const firstOption = variantGroup
                ? variantGroup.querySelector('[data-kussin-variant-value]:not(:disabled)')
                : null;

            if (!variantGroup) {
                return;
            }

            event.preventDefault();
            variantGroup.scrollIntoView({
                behavior: reducedMotion.matches ? 'auto' : 'smooth',
                block: 'center',
            });
            firstOption?.focus({ preventScroll: true });
        });
        window.addEventListener('resize', requestRefresh, { passive: true });

        if (typeof mobileViewport.addEventListener === 'function') {
            mobileViewport.addEventListener('change', updateState);
        } else {
            mobileViewport.addListener(updateState);
        }

        bindCurrentCart();
    }

    function initProductInformationPanels() {
        if (document.documentElement.dataset.kussinProductPanelsBound === '1') {
            return;
        }

        document.documentElement.dataset.kussinProductPanelsBound = '1';
        document.addEventListener('click', (event) => {
            const trigger = event.target.closest
                ? event.target.closest('.kussin-product-information-trigger, [data-kussin-product-panel-open]')
                : null;
            const panel = trigger ? trigger.closest('[data-kussin-product-panel]') : null;
            const headingTrigger = panel ? panel.querySelector('.kussin-product-information-trigger') : null;
            const content = panel ? panel.querySelector('[data-kussin-product-panel-content]') : null;

            if (!trigger || !panel || !headingTrigger) {
                return;
            }

            const open = trigger.hasAttribute('data-kussin-product-panel-open')
                || headingTrigger.getAttribute('aria-expanded') !== 'true';

            panel.classList.toggle('is-open', open);
            headingTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
            if (content) {
                const previewVisible = panel.classList.contains('kussin-product-information-panel-preview');
                content.setAttribute('aria-hidden', open || previewVisible ? 'false' : 'true');
            }
        });
    }

    function createInertInfiniteClone(source, position) {
        const sourceTag = source.tagName.toLowerCase();
        const clone = document.createElement(sourceTag.includes('-') ? 'div' : sourceTag);

        clone.className = source.className;
        clone.innerHTML = source.innerHTML;
        clone.dataset.kussinInfiniteClone = position;
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('inert', '');

        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone.querySelectorAll('[name]').forEach((element) => element.removeAttribute('name'));
        clone.querySelectorAll('input, select, textarea, button').forEach((control) => {
            control.disabled = true;
        });
        // #69726: a cloned video slide must not load a second player or a second consent
        // placeholder. The clone is inert and only shows while the scroller wraps around.
        clone.querySelectorAll('iframe, video, .cmplazypreviewiframe').forEach((media) => media.remove());

        return clone;
    }

    /* #69726: video slides in the product gallery. Three things the picture slides do not need: a
       keyboard can pick a video thumbnail, the YouTube still is fetched into the thumbnail only after
       the consent manager has released the slide (#70351: no YouTube request before that), and a
       video that scrolls or fades out of view stops playing. */
    function initProductGalleryVideos() {
        if (document.documentElement.dataset.kussinProductGalleryVideosBound === '1') {
            return;
        }

        document.documentElement.dataset.kussinProductGalleryVideosBound = '1';

        const videoFigures = (gallery) => Array.from(
            gallery.querySelectorAll('.wrap > figure[data-kussin-product-gallery-video]:not([data-kussin-infinite-clone])'),
        );

        const frameReleased = (frame) => {
            const src = frame.getAttribute('src') || '';

            return src !== '' && src !== 'about:blank';
        };

        // Consent is per vendor: once one YouTube frame carries its real src, every YouTube
        // thumbnail of the gallery may load its still.
        const releaseThumbnails = (gallery) => {
            const released = videoFigures(gallery)
                .filter((figure) => figure.dataset.kussinProductGalleryVideo === 'youtube')
                .some((figure) => Array.from(figure.querySelectorAll('iframe')).some(frameReleased));

            if (!released) {
                return;
            }

            gallery.querySelectorAll('img[data-kussin-product-gallery-video-thumb]').forEach((image) => {
                const still = image.dataset.kussinProductGalleryVideoThumb;

                if (still && image.getAttribute('src') !== still) {
                    image.setAttribute('src', still);
                }
            });
        };

        const stopVideo = (figure) => {
            figure.querySelectorAll('video').forEach((video) => {
                if (!video.paused) {
                    video.pause();
                }
            });
            // The YouTube player offers no pause hook without its API script, reloading the frame
            // stops sound and picture. Only a released frame that has been on screen is reloaded.
            figure.querySelectorAll('iframe').forEach((frame) => {
                if (frameReleased(frame) && figure.dataset.kussinProductGalleryVideoShown === '1') {
                    figure.dataset.kussinProductGalleryVideoShown = '0';
                    frame.setAttribute('src', frame.getAttribute('src'));
                }
            });
        };

        const watchVisibility = (gallery) => {
            const wrap = gallery.querySelector('.wrap');

            if (!wrap || typeof IntersectionObserver !== 'function'
                || gallery.dataset.kussinProductGalleryVideosWatched === '1') {
                return;
            }

            gallery.dataset.kussinProductGalleryVideosWatched = '1';

            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                        entry.target.dataset.kussinProductGalleryVideoShown = '1';
                        return;
                    }

                    stopVideo(entry.target);
                });
            }, { root: wrap, threshold: [0.5] });

            videoFigures(gallery).forEach((figure) => observer.observe(figure));
        };

        const bind = (root = document) => {
            const galleries = [];

            if (root instanceof Element && root.matches('[data-kussin-product-gallery]')) {
                galleries.push(root);
            }

            if (root.querySelectorAll) {
                galleries.push(...root.querySelectorAll('[data-kussin-product-gallery]'));
            }

            [...new Set(galleries)].forEach((gallery) => {
                watchVisibility(gallery);
                releaseThumbnails(gallery);
            });
        };

        document.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') {
                return;
            }

            const thumb = event.target instanceof Element
                ? event.target.closest('.kussin-product-gallery-video-thumb')
                : null;

            if (!thumb) {
                return;
            }

            event.preventDefault();
            thumb.click();
        });

        if (typeof MutationObserver === 'function') {
            // consentmanager, or the no-CMP fallback from wh1-core-modifications, releases a gated
            // frame by writing its src. That is the moment the thumbnails may follow.
            new MutationObserver(() => bind()).observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['src'],
                subtree: true,
            });
        }

        document.addEventListener('dom-updated', (event) => {
            bind(event.target instanceof Element ? event.target : document);
        });
        bind();
    }

    function initProductGallery() {
        if (document.documentElement.dataset.kussinProductGalleryBound === '1') {
            return;
        }

        document.documentElement.dataset.kussinProductGalleryBound = '1';

        const mobileViewport = window.matchMedia('(max-width: 767.98px)');
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        const scheduledGalleries = new WeakSet();
        const scheduledGalleryRefreshRoots = new WeakSet();
        const galleryTouchStarts = new WeakMap();
        const galleryCloneSources = new WeakMap();

        const galleryIsInfinite = (gallery) => (
            gallery.hasAttribute('data-kussin-product-gallery-infinite')
        );

        const galleryFigures = (wrap) => Array.from(
            wrap.querySelectorAll(':scope > figure:not([data-kussin-infinite-clone])'),
        );

        const syncGalleryClones = (gallery) => {
            const wrap = gallery.querySelector('.wrap');

            if (!wrap) {
                return false;
            }

            const figures = galleryFigures(wrap);
            const existingClones = wrap.querySelectorAll(':scope > [data-kussin-infinite-clone]');
            const shouldClone = mobileViewport.matches && galleryIsInfinite(gallery) && figures.length > 1;
            const previousSources = galleryCloneSources.get(gallery);
            const sourcesUnchanged = shouldClone
                && previousSources
                && previousSources.first === figures[0]
                && previousSources.last === figures[figures.length - 1]
                && previousSources.count === figures.length
                && existingClones.length === 2;

            if (sourcesUnchanged) {
                return false;
            }

            existingClones.forEach((clone) => clone.remove());
            galleryCloneSources.delete(gallery);

            if (!shouldClone) {
                return existingClones.length > 0;
            }

            wrap.prepend(createInertInfiniteClone(figures[figures.length - 1], 'before'));
            wrap.append(createInertInfiniteClone(figures[0], 'after'));
            galleryCloneSources.set(gallery, {
                first: figures[0],
                last: figures[figures.length - 1],
                count: figures.length,
            });

            return true;
        };

        const normalizeImageIndex = (gallery, requestedIndex) => {
            const count = gallery.querySelectorAll('.gallery-helper').length;

            if (count === 0) {
                return 0;
            }

            if (galleryIsInfinite(gallery) && count > 1) {
                return ((requestedIndex % count) + count) % count;
            }

            return Math.max(0, Math.min(requestedIndex, count - 1));
        };

        const setActiveImage = (gallery, index) => {
            const dots = Array.from(gallery.querySelectorAll('[data-kussin-product-gallery-dot]'));
            const controls = Array.from(gallery.querySelectorAll('.gallery-helper'));
            const activeIndex = normalizeImageIndex(gallery, index);

            dots.forEach((dot, dotIndex) => {
                const active = dotIndex === activeIndex;
                dot.classList.toggle('is-active', active);
                dot.setAttribute('aria-current', active ? 'true' : 'false');
            });

            if (controls[activeIndex]) {
                controls[activeIndex].checked = true;
            }
        };

        const closestImageIndex = (wrap) => {
            const wrapBounds = wrap.getBoundingClientRect();
            const wrapCenter = wrapBounds.left + (wrapBounds.width / 2);
            const figures = galleryFigures(wrap);
            let closestIndex = 0;
            let closestDistance = Number.POSITIVE_INFINITY;

            figures.forEach((figure, index) => {
                const bounds = figure.getBoundingClientRect();
                const distance = Math.abs((bounds.left + (bounds.width / 2)) - wrapCenter);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });

            return closestIndex;
        };

        const syncGallery = (wrap) => {
            const gallery = wrap.closest('[data-kussin-product-gallery]');

            if (!gallery) {
                return;
            }

            setActiveImage(gallery, closestImageIndex(wrap));
        };

        const scrollToImage = (gallery, requestedIndex, instant = false) => {
            const wrap = gallery.querySelector('.wrap');
            const figures = wrap ? galleryFigures(wrap) : [];
            const index = normalizeImageIndex(gallery, requestedIndex);
            const figure = figures[index];

            if (!wrap || !figure) {
                return;
            }

            const wrapBounds = wrap.getBoundingClientRect();
            const figureBounds = figure.getBoundingClientRect();
            let left = figureBounds.left - wrapBounds.left + wrap.scrollLeft;

            if (mobileViewport.matches) {
                left -= (wrap.clientWidth - figureBounds.width) / 2;
            }

            wrap.scrollTo({
                left,
                behavior: instant ? 'instant' : (reducedMotion.matches ? 'auto' : 'smooth'),
            });
            setActiveImage(gallery, index);
        };

        const alignGalleryToCheckedImage = (gallery, instant = false) => {
            const controls = Array.from(gallery.querySelectorAll('.gallery-helper'));
            const activeIndex = Math.max(0, controls.findIndex((control) => control.checked));

            scrollToImage(gallery, activeIndex, instant);
        };

        document.addEventListener('click', (event) => {
            const dot = event.target.closest
                ? event.target.closest('[data-kussin-product-gallery-dot]')
                : null;
            const gallery = dot ? dot.closest('[data-kussin-product-gallery]') : null;

            if (!dot || !gallery || !mobileViewport.matches) {
                return;
            }

            scrollToImage(gallery, Number(dot.dataset.kussinProductGalleryDot));
        });

        document.addEventListener('change', (event) => {
            const control = event.target.closest
                ? event.target.closest('[data-kussin-product-gallery] .gallery-helper')
                : null;
            const gallery = control ? control.closest('[data-kussin-product-gallery]') : null;

            if (!control || !gallery) {
                return;
            }

            const controls = Array.from(gallery.querySelectorAll('.gallery-helper'));
            const activeIndex = controls.indexOf(control);

            setActiveImage(gallery, activeIndex);

            if (mobileViewport.matches) {
                scrollToImage(gallery, activeIndex);
            }
        });

        document.addEventListener('scroll', (event) => {
            const wrap = event.target instanceof Element
                && event.target.matches('[data-kussin-product-gallery] .wrap')
                ? event.target
                : null;

            if (!wrap || !mobileViewport.matches || scheduledGalleries.has(wrap)) {
                return;
            }

            scheduledGalleries.add(wrap);
            window.requestAnimationFrame(() => {
                syncGallery(wrap);
                scheduledGalleries.delete(wrap);
            });
        }, true);

        document.addEventListener('touchstart', (event) => {
            const wrap = event.target.closest
                ? event.target.closest('[data-kussin-product-gallery] .wrap')
                : null;
            const gallery = wrap ? wrap.closest('[data-kussin-product-gallery]') : null;
            const touch = event.touches.length === 1 ? event.touches[0] : null;

            if (!wrap || !gallery || !touch || !galleryIsInfinite(gallery)) {
                return;
            }

            galleryTouchStarts.set(wrap, {
                x: touch.clientX,
                y: touch.clientY,
                index: closestImageIndex(wrap),
            });
        }, { passive: true });

        document.addEventListener('touchend', (event) => {
            const wrap = event.target.closest
                ? event.target.closest('[data-kussin-product-gallery] .wrap')
                : null;
            const gallery = wrap ? wrap.closest('[data-kussin-product-gallery]') : null;
            const start = wrap ? galleryTouchStarts.get(wrap) : null;
            const touch = event.changedTouches.length === 1 ? event.changedTouches[0] : null;

            if (!wrap || !gallery || !start || !touch) {
                return;
            }

            galleryTouchStarts.delete(wrap);

            const deltaX = touch.clientX - start.x;
            const deltaY = touch.clientY - start.y;
            const lastIndex = galleryFigures(wrap).length - 1;

            if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) {
                return;
            }

            if (start.index === 0 && deltaX > 0) {
                scrollToImage(gallery, lastIndex, true);
            } else if (start.index === lastIndex && deltaX < 0) {
                scrollToImage(gallery, 0, true);
            }
        }, { passive: true });

        const refreshGalleries = (root = document, align = false) => {
            const galleries = [];

            if (root instanceof Element && root.matches('[data-kussin-product-gallery]')) {
                galleries.push(root);
            }

            if (root.querySelectorAll) {
                galleries.push(...root.querySelectorAll('[data-kussin-product-gallery]'));
            }

            [...new Set(galleries)].forEach((gallery) => {
                const clonesChanged = syncGalleryClones(gallery);

                if (mobileViewport.matches && (align || clonesChanged)) {
                    alignGalleryToCheckedImage(gallery, true);
                }
            });
        };

        if (typeof mobileViewport.addEventListener === 'function') {
            mobileViewport.addEventListener('change', () => refreshGalleries(document, true));
        } else {
            mobileViewport.addListener(() => refreshGalleries(document, true));
        }

        document.addEventListener('dom-updated', (event) => {
            const root = event.target instanceof Element ? event.target : document;
            const owningGallery = root instanceof Element
                ? root.closest('[data-kussin-product-gallery]')
                : null;
            const refreshRoot = owningGallery || root;
            const containsGallery = Boolean(owningGallery)
                || (root.querySelector && root.querySelector('[data-kussin-product-gallery]'));

            if (!containsGallery || scheduledGalleryRefreshRoots.has(refreshRoot)) {
                return;
            }

            scheduledGalleryRefreshRoots.add(refreshRoot);
            window.requestAnimationFrame(() => {
                refreshGalleries(refreshRoot);
                scheduledGalleryRefreshRoots.delete(refreshRoot);
            });
        });

        refreshGalleries(document, true);
    }

    /**
     * #70006 — make the basket's notice-list star say what it is doing.
     *
     * APEX renders the same outline star with the same "copy" wording whether or not the article is
     * already on the list, and its handler ends in a bare `window.location.reload()`
     * (out/…/movetonoticelist.min.js). Adding worked, but the page just blinked: no message, no
     * change of state — which is why the ticket kept coming back as "it does not work".
     *
     * The shop emits the ids that are on the list (see layout/base.html.twig). From those:
     *   - the icon becomes `#star-fill`, which the sprite already ships and nothing used;
     *   - the label and title become "remove", because a second click DOES remove it — the handler
     *     sends `data-amount`, and OXID's `UserBasket::addItemToBasket()` deletes the item when the
     *     amount is 0, so the amount is set to 0 for an article that is already there;
     *   - a confirmation is shown after the reload.
     *
     * The message survives the reload in `sessionStorage`, written on click and read once on load.
     * It is deliberately not written optimistically into the DOM: the reload re-renders from the
     * shop, so what is finally shown is the server's state, not an assumption about it.
     */
    function initBasketNoticeList() {
        const buttons = document.querySelectorAll('button.btn-noticelist[data-articleid], button.btn-noticelist[data-articleId]');
        if (!buttons.length) {
            return;
        }
        const onList = Array.isArray(window.WH1_NOTICELIST) ? window.WH1_NOTICELIST : [];
        const i18n = window.WH1_NOTICELIST_I18N || {};
        const STORAGE_KEY = 'wh1NoticeListResult';

        buttons.forEach((button) => {
            const articleId = button.getAttribute('data-articleId') || button.getAttribute('data-articleid');
            const isOnList = onList.indexOf(articleId) !== -1;

            if (isOnList) {
                const icon = button.querySelector('use');
                if (icon) {
                    icon.setAttribute('xlink:href', '#star-fill');
                }
                // A second click removes it: the handler forwards this amount and 0 means "delete".
                button.setAttribute('data-amount', '0');
                if (i18n.remove) {
                    button.setAttribute('title', i18n.remove);
                    // The mobile control is a dropdown entry and carries a visible label; the
                    // desktop one is icon-only, so only replace text that is actually there.
                    if ((button.textContent || '').trim()) {
                        const label = [...button.childNodes].find((node) => node.nodeType === 3 && node.textContent.trim());
                        if (label) {
                            label.textContent = ' ' + i18n.remove + ' ';
                        }
                    }
                }
            }

            button.addEventListener('click', () => {
                try {
                    window.sessionStorage.setItem(STORAGE_KEY, isOnList ? 'removed' : 'added');
                } catch (e) {
                    // private mode — the action still works, only the confirmation is lost
                }
            });
        });

        let result = null;
        try {
            result = window.sessionStorage.getItem(STORAGE_KEY);
            window.sessionStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            result = null;
        }
        if (!result) {
            return;
        }
        const text = result === 'removed' ? i18n.removed : i18n.added;
        if (!text) {
            return;
        }
        const anchor = document.getElementById('basket_form') || document.querySelector('.card');
        if (!anchor || !anchor.parentNode) {
            return;
        }
        const alert = document.createElement('div');
        alert.className = 'alert alert-success wh1-noticelist-message';
        alert.setAttribute('role', 'status');
        alert.textContent = text;
        anchor.parentNode.insertBefore(alert, anchor);
    }

    /**
     * #70051 / #70062 — checkout step 1 ("Adresse") form validation.
     *
     * Two defects sit on top of each other:
     *
     *  1. `oe/gdpr-optin-module` rebinds the "Weiter" button
     *     (`views/twig/extensions/themes/default/form/user_checkout_change.html.twig:102-144`)
     *     and finishes with a bare `document.querySelector("#user_form").submit()`.
     *     `HTMLFormElement.submit()` skips HTML5 constraint validation *and*
     *     fires no `submit` event, so APEX's `.needs-validation` gate
     *     (`apex-theme/build/js/main.js:33-66`) is bypassed and an incomplete
     *     form is always POSTed.
     *  2. The server therefore answers with a re-rendered page in which
     *     `form/fieldset/user_billing.html.twig` has stamped `is-invalid` on
     *     the `.form-floating` wrapper. That class is static markup — nothing
     *     removes it — so a field the customer has corrected keeps its red
     *     border and its "Bitte Wert angeben!" message. That is #70051.
     *
     * This validates before the other two handlers get the click, keeps the
     * customer on the page when something is missing, and clears the stale
     * red state live as soon as a field becomes valid again.
     *
     * #69986 made it shared. The contact form had the same "nothing happens
     * until you press the button" behaviour for a different reason: APEX marks
     * `.needs-validation` forms `novalidate` and only adds `was-validated` from
     * its own `submit` handler (`apex-theme/build/js/main.js:33-66`), which is
     * the Bootstrap 5 pattern — no field can look wrong before the first
     * submit. `validateOnBlur` adds the missing half: a field the customer has
     * filled in and left is checked there and then.
     *
     * @param {HTMLFormElement} form
     * @param {Element} trigger  The control that sends the form. Passed as an
     *        element rather than looked up inside `form`, because checkout's
     *        "Weiter" button sits in the summary column, outside `#user_form`.
     * @param {{validateOnBlur?: boolean}} options
     */
    function bindLiveFormValidation(form, trigger, options) {
        const validateOnBlur = options.validateOnBlur === true;

        if (!form || !trigger || form.dataset.wh1ValidationBound === '1') {
            return;
        }

        form.dataset.wh1ValidationBound = '1';

        const isRendered = (element) =>
            !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);

        /** The box the shop marks with `is-invalid` for a given control. */
        const wrapperOf = (element) =>
            element.closest('.form-floating') || element.closest('.mb-3') || element.parentElement;

        const messageFor = (element) => {
            const i18n = (window.oApex && window.oApex.i18n) || {};

            if (element.validity.valueMissing) {
                return element.tagName === 'SELECT'
                    ? i18n.DD_FORM_VALIDATION_CHECKONE || element.validationMessage
                    : i18n.DD_FORM_VALIDATION_REQUIRED || element.validationMessage;
            }

            if (element.validity.typeMismatch) {
                return i18n.DD_FORM_VALIDATION_VALIDEMAIL || element.validationMessage;
            }

            return element.validationMessage;
        };

        const markInvalid = (element) => {
            const wrapper = wrapperOf(element);
            if (!wrapper) {
                return;
            }

            wrapper.classList.add('is-invalid');

            const next = wrapper.nextElementSibling;

            // A message the server rendered for this field wins — it is the
            // shop's own wording. Otherwise keep one message element around and
            // only rewrite its text, so nothing is added to or removed from the
            // DOM after the first failure (#70062).
            if (next && next.classList.contains('invalid-feedback')) {
                if (next.classList.contains('wh1-invalid-feedback')) {
                    next.textContent = messageFor(element);
                }
                return;
            }

            const feedback = document.createElement('div');
            feedback.className = 'invalid-feedback wh1-invalid-feedback';
            feedback.textContent = messageFor(element);
            wrapper.after(feedback);
        };

        const clearInvalid = (element) => {
            const wrapper = wrapperOf(element);
            if (wrapper) {
                wrapper.classList.remove('is-invalid');
            }
            element.classList.remove('is-invalid');
        };

        /**
         * Collect the controls that are both visible and invalid.
         *
         * APEX un-requires hidden controls by looking at inline `style.display`
         * only (`main.js:138-150`), which misses anything hidden by a class.
         * Actual rendering is the reliable test, so use that — and put the
         * `required` flags back afterwards so nothing leaks into the POST.
         */
        const collectInvalid = () => {
            const controls = Array.prototype.slice.call(
                form.querySelectorAll('input, select, textarea')
            );
            const relaxed = [];

            controls.forEach((element) => {
                element.setCustomValidity('');

                if (element.required && !isRendered(element)) {
                    element.required = false;
                    relaxed.push(element);
                }
            });

            // `validity.valid` is read-only and, unlike checkValidity(), fires
            // no `invalid` event — APEX listens for none, but neither should we.
            const invalid = controls.filter(
                (element) => element.willValidate && isRendered(element) && !element.validity.valid
            );

            relaxed.forEach((element) => {
                element.required = true;
            });

            return invalid;
        };

        // Capture phase on `document`: this runs before any listener bound to
        // the button itself, so both APEX's and the GDPR module's handlers can
        // be stopped when the form is not fit to send.
        document.addEventListener(
            'click',
            (event) => {
                const target = event.target instanceof Element ? event.target : null;
                if (!target || (target !== trigger && !trigger.contains(target))) {
                    return;
                }

                const invalid = collectInvalid();

                if (!invalid.length) {
                    return;
                }

                event.preventDefault();
                event.stopImmediatePropagation();

                invalid.forEach(markInvalid);

                // Deliberately no focus() here: on a phone that pops the virtual
                // keyboard, which resizes the viewport and moves the page under
                // the customer's finger — the very thing #70062 is about.
                invalid[0].scrollIntoView({
                    block: 'center',
                    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                        ? 'auto'
                        : 'smooth',
                });
            },
            true
        );

        const revalidate = (event) => {
            const element = event.target;

            if (
                !(element instanceof Element) ||
                !element.matches('input, select, textarea') ||
                !element.willValidate
            ) {
                return;
            }

            // APEX's message helper leaves a custom validity behind, which keeps
            // a filled field `:invalid` for good; drop it on every edit.
            element.setCustomValidity('');

            if (element.validity.valid) {
                clearInvalid(element);
            }
        };

        form.addEventListener('input', revalidate, true);
        form.addEventListener('change', revalidate, true);

        if (!validateOnBlur) {
            return;
        }

        // Only a control the customer has actually filled in is judged on the
        // way out, plus one that is already marked. Flagging an empty required
        // field the moment it is tabbed past would paint the form red before
        // anyone had a chance to fill it in — that case is the submit click's.
        form.addEventListener(
            'blur',
            (event) => {
                const element = event.target;

                if (
                    !(element instanceof Element) ||
                    !element.matches('input, select, textarea') ||
                    !element.willValidate ||
                    element.validity.valid
                ) {
                    return;
                }

                const wrapper = wrapperOf(element);
                const alreadyMarked = wrapper && wrapper.classList.contains('is-invalid');

                if (alreadyMarked || String(element.value).trim() !== '') {
                    markInvalid(element);
                }
            },
            true
        );
    }

    function initCheckoutFormValidation() {
        bindLiveFormValidation(
            document.getElementById('user_form'),
            document.getElementById('userFormSubmit'),
            {}
        );
    }

    function initContactFormValidation() {
        document.querySelectorAll('form.wh1-contact-form').forEach((form) => {
            bindLiveFormValidation(form, form.querySelector('.wh1-contact-submit'), {
                validateOnBlur: true,
            });
        });
    }

    /**
     * #70286 — hide a recommendation section that never gets any products.
     *
     * "Kunden kauften auch" and its siblings are rendered by FACT-Finder's web components: the
     * shop prints the heading and an empty slider, and the component fills it once the API answers.
     * When the API answers with `"hits": []` — an article the index does not know, which is every
     * article on an environment whose export has stalled — nothing arrives and the section stands
     * there as a band of empty page between the content and the footer.
     *
     * The section is therefore collapsed while it holds no record, and revealed the moment one
     * appears. The observer is what makes it safe: the records arrive long after DOMContentLoaded,
     * and on a slow answer a one-shot check would hide a section that is about to be filled.
     */
    function initEmptyCarouselCollapse() {
        const sections = document.querySelectorAll('.details-related, .kussin-factfinder-pdp-related');

        if (!sections.length) {
            return;
        }

        const RECORD_SELECTOR = [
            '.ff-record-list-item',
            '.ff-record-item',
            'ff-record',
            '.kussin-product-carousel-slide',
            '.product-card',
        ].join(',');

        const update = (section) => {
            const hasRecords = section.querySelector(RECORD_SELECTOR) !== null;

            section.classList.toggle('wh1-carousel-empty', !hasRecords);
        };

        sections.forEach((section) => {
            update(section);

            const observer = new MutationObserver(() => update(section));

            observer.observe(section, { childList: true, subtree: true });
        });
    }

    function initCarouselSizeOverlay() {
        if (document.documentElement.dataset.kussinCarouselSizesBound === '1') {
            return;
        }
        document.documentElement.dataset.kussinCarouselSizesBound = '1';

        const desktop = window.matchMedia('(min-width: 992px) and (hover: hover)');
        const selector = '[data-kussin-product-carousel] .kussin-factfinder-record-card';
        const panel = document.createElement('div');
        panel.className = 'kussin-carousel-size-overlay';
        panel.id = 'kussin-carousel-size-overlay';
        panel.setAttribute('role', 'tooltip');
        panel.hidden = true;
        document.body.append(panel);
        let active = null;
        let describedLink = null;
        let previousDescription = null;
        let observer = null;

        const close = () => {
            if (describedLink) {
                if (previousDescription === null) {
                    describedLink.removeAttribute('aria-describedby');
                } else {
                    describedLink.setAttribute('aria-describedby', previousDescription);
                }
            }
            observer?.disconnect();
            active?.classList.remove('kussin-carousel-size-open');
            active = null;
            describedLink = null;
            panel.hidden = true;
            panel.replaceChildren();
        };

        const open = (card) => {
            if (card === active) {
                return;
            }
            close();
            if (!desktop.matches || !card || card.closest('[data-kussin-infinite-clone]')
                || (document.body.matches('.cl-alist, .cl-search, .cl-manufacturerlist')
                    && card.closest('.kussin-factfinder-campaign-pushed-products'))) {
                return;
            }
            const sizes = card.querySelectorAll('.kussin-factfinder-record-variants ul.sizeselection li');
            const rect = card.getBoundingClientRect();
            const track = card.closest('[data-kussin-product-carousel-track]');
            const bounds = track?.getBoundingClientRect();
            if (!sizes.length || !bounds || rect.left < bounds.left - 1
                || rect.right > bounds.right + 1 || rect.bottom < 0 || rect.bottom >= window.innerHeight) {
                return;
            }

            // These chips describe available sizes; the product link owns navigation.
            // Copy text only, avoiding duplicate IDs, controls, and Web Component bindings.
            const list = document.createElement('ul');
            sizes.forEach((size) => {
                const item = document.createElement('li');
                item.textContent = size.textContent;
                item.classList.toggle('sold-out', size.classList.contains('sold-out'));
                list.append(item);
            });
            panel.append(list);
            panel.style.left = `${rect.left}px`;
            panel.style.top = `${rect.bottom - 1}px`;
            panel.style.width = `${rect.width}px`;
            panel.style.maxHeight = `${window.innerHeight - rect.bottom}px`;
            panel.hidden = false;
            active = card;
            active.classList.add('kussin-carousel-size-open');
            describedLink = card.querySelector('.kussin-factfinder-record-link');
            previousDescription = describedLink?.getAttribute('aria-describedby') ?? null;
            describedLink?.setAttribute('aria-describedby',
                [previousDescription, panel.id].filter(Boolean).join(' '));
            // Close if asynchronous records replace or change the active card.
            observer = new MutationObserver(close);
            observer.observe(track, { childList: true, subtree: true, characterData: true });
        };

        const cardAt = (target) => target instanceof Element ? target.closest(selector) : null;
        document.addEventListener('pointerover', (event) => {
            if (event.pointerType !== 'touch' && !panel.contains(event.target)) {
                const card = cardAt(event.target);
                if (card) open(card);
            }
        });
        document.addEventListener('pointerout', (event) => {
            if (!active || (!active.contains(event.target) && !panel.contains(event.target))) return;
            if (event.relatedTarget instanceof Node
                && (active.contains(event.relatedTarget) || panel.contains(event.relatedTarget))) return;
            if (!active.contains(document.activeElement)) close();
        });
        document.addEventListener('focusin', (event) => open(cardAt(event.target)));
        document.addEventListener('focusout', () => {
            queueMicrotask(() => {
                if (active && !active.contains(document.activeElement) && !active.matches(':hover')
                    && !panel.matches(':hover')) close();
            });
        });
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') close();
        });
        document.addEventListener('scroll', (event) => {
            if (!panel.contains(event.target)) close();
        }, true);
        window.addEventListener('resize', close, { passive: true });
        desktop.addEventListener('change', close);
        document.addEventListener('dom-updated', close);
    }

    function initProductCarousels() {
        initCarouselSizeOverlay();
        const mobileViewport = window.matchMedia('(max-width: 767.98px)');
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        const scheduledTracks = new WeakSet();
        const carouselTouchStarts = new WeakMap();
        const carouselCloneSources = new WeakMap();
        let resizeRequested = false;

        const carouselIsInfinite = (carousel) => (
            carousel.hasAttribute('data-kussin-product-carousel-infinite')
        );

        const carouselSlides = (carousel) => {
            const track = carousel.querySelector('[data-kussin-product-carousel-track]');

            return track
                ? Array.from(track.children).filter((slide) => (
                    slide.classList.contains('kussin-product-carousel-slide')
                    && !slide.hasAttribute('data-kussin-infinite-clone')
                ))
                : [];
        };

        const itemsPerPage = (carousel) => {
            if (mobileViewport.matches) {
                return 1;
            }

            return Math.max(1, Number(carousel.dataset.kussinProductCarouselDesktopItems) || 1);
        };

        const scrollItems = (carousel) => mobileViewport.matches ? 1 : Math.max(
            1,
            Math.min(itemsPerPage(carousel), Math.floor(Number(
                carousel.dataset.kussinProductCarouselScrollItems,
            )) || itemsPerPage(carousel)),
        );

        const pageCount = (carousel) => Math.max(
            1,
            Math.ceil((carouselSlides(carousel).length - itemsPerPage(carousel)) / scrollItems(carousel)) + 1,
        );

        const normalizePage = (carousel, requestedPage) => {
            const count = pageCount(carousel);

            if (carouselIsInfinite(carousel) && count > 1) {
                return ((requestedPage % count) + count) % count;
            }

            return Math.max(0, Math.min(requestedPage, count - 1));
        };

        const currentPage = (carousel) => {
            const track = carousel.querySelector('[data-kussin-product-carousel-track]');
            const slides = carouselSlides(carousel);

            if (!track || slides.length === 0) {
                return 0;
            }

            if (!mobileViewport.matches) {
                const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);

                return maxScroll > 0
                    ? Math.round((track.scrollLeft / maxScroll) * (pageCount(carousel) - 1))
                    : 0;
            }

            const trackCenter = track.getBoundingClientRect().left + (track.clientWidth / 2);
            let closestIndex = 0;
            let closestDistance = Number.POSITIVE_INFINITY;

            slides.forEach((slide, index) => {
                const bounds = slide.getBoundingClientRect();
                const distance = Math.abs((bounds.left + (bounds.width / 2)) - trackCenter);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });

            return closestIndex;
        };

        const updateCarouselState = (carousel) => {
            const track = carousel.querySelector('[data-kussin-product-carousel-track]');
            const previous = carousel.querySelector('[data-kussin-product-carousel-previous]');
            const next = carousel.querySelector('[data-kussin-product-carousel-next]');
            const dots = Array.from(carousel.querySelectorAll('[data-kussin-product-carousel-page]'));

            if (!track) {
                return;
            }

            const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
            const hasOverflow = maxScroll > 2;
            const activePage = Math.max(0, Math.min(currentPage(carousel), pageCount(carousel) - 1));
            const canLoop = carouselIsInfinite(carousel) && pageCount(carousel) > 1;

            if (previous) {
                previous.hidden = !hasOverflow;
                previous.disabled = !hasOverflow || (!canLoop && track.scrollLeft <= 2);
            }

            if (next) {
                next.hidden = !hasOverflow;
                next.disabled = !hasOverflow || (!canLoop && track.scrollLeft >= maxScroll - 2);
            }

            dots.forEach((dot, index) => {
                dot.setAttribute('aria-current', index === activePage ? 'true' : 'false');
            });
        };

        const buildCarouselDots = (carousel) => {
            const dots = carousel.querySelector('[data-kussin-product-carousel-dots]');

            if (!dots) {
                return;
            }

            const count = pageCount(carousel);
            const labelTemplate = dots.dataset.kussinProductCarouselDotLabel || 'Product %current% of %total%';

            dots.replaceChildren();

            for (let index = 0; index < count; index += 1) {
                const dot = document.createElement('button');
                const label = labelTemplate
                    .replace('%current%', String(index + 1))
                    .replace('%total%', String(count));

                dot.type = 'button';
                dot.className = 'kussin-product-carousel-dot';
                dot.dataset.kussinProductCarouselPage = String(index);
                dot.setAttribute('aria-label', label);
                dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
                dots.append(dot);
            }

            dots.hidden = count <= 1;
        };

        const scrollToPage = (carousel, requestedPage, instant = false) => {
            const track = carousel.querySelector('[data-kussin-product-carousel-track]');
            const slides = carouselSlides(carousel);
            const count = pageCount(carousel);
            const targetPage = normalizePage(carousel, requestedPage);
            const targetIndex = Math.min(targetPage * scrollItems(carousel), slides.length - 1);
            const target = slides[targetIndex];

            if (!track || !target) {
                return;
            }

            const trackBounds = track.getBoundingClientRect();
            const targetBounds = target.getBoundingClientRect();
            let left = targetBounds.left - trackBounds.left + track.scrollLeft;

            if (mobileViewport.matches) {
                left -= (track.clientWidth - targetBounds.width) / 2;
            }

            track.scrollTo({
                left,
                behavior: instant || requestedPage < 0 || requestedPage >= count || reducedMotion.matches
                    ? (reducedMotion.matches ? 'auto' : 'instant')
                    : 'smooth',
            });
            window.requestAnimationFrame(() => updateCarouselState(carousel));
        };

        const syncCarouselClones = (carousel) => {
            const track = carousel.querySelector('[data-kussin-product-carousel-track]');

            if (!track) {
                return false;
            }

            const slides = carouselSlides(carousel);
            const existingClones = track.querySelectorAll(':scope > [data-kussin-infinite-clone]');
            const shouldClone = mobileViewport.matches && carouselIsInfinite(carousel) && slides.length > 1;
            const previousSources = carouselCloneSources.get(carousel);
            const sourcesUnchanged = shouldClone
                && previousSources
                && previousSources.first === slides[0]
                && previousSources.last === slides[slides.length - 1]
                && previousSources.count === slides.length
                && existingClones.length === 2;

            if (sourcesUnchanged) {
                return false;
            }

            existingClones.forEach((clone) => clone.remove());
            carouselCloneSources.delete(carousel);

            if (!shouldClone) {
                return existingClones.length > 0;
            }

            track.prepend(createInertInfiniteClone(slides[slides.length - 1], 'before'));
            track.append(createInertInfiniteClone(slides[0], 'after'));
            carouselCloneSources.set(carousel, {
                first: slides[0],
                last: slides[slides.length - 1],
                count: slides.length,
            });

            return true;
        };

        const refreshCarousel = (carousel) => {
            const track = carousel.querySelector('[data-kussin-product-carousel-track]');

            if (!track) {
                return;
            }

            carousel.style.setProperty(
                '--kussin-product-carousel-desktop-items',
                String(itemsPerPage(carousel)),
            );

            const activePage = currentPage(carousel);
            const clonesChanged = syncCarouselClones(carousel);

            buildCarouselDots(carousel);
            if (clonesChanged) {
                scrollToPage(carousel, activePage, true);
            } else {
                updateCarouselState(carousel);
            }
        };

        const refreshCarousels = (root = document) => {
            const carousels = [];

            if (root instanceof Element && root.matches('[data-kussin-product-carousel]')) {
                carousels.push(root);
            }

            if (root.querySelectorAll) {
                carousels.push(...root.querySelectorAll('[data-kussin-product-carousel]'));
            }

            [...new Set(carousels)].forEach(refreshCarousel);
        };

        const resetCarouselPositions = () => {
            document.querySelectorAll('[data-kussin-product-carousel-track]').forEach((track) => {
                track.scrollTo({ left: 0, behavior: 'auto' });
            });
            refreshCarousels();
        };

        if (document.documentElement.dataset.kussinProductCarouselBound !== '1') {
            document.documentElement.dataset.kussinProductCarouselBound = '1';

            // Image-only category cards can have no height until their artwork
            // loads. Refresh overflow controls once their geometry is available.
            document.addEventListener('load', (event) => {
                const carousel = event.target instanceof HTMLImageElement
                    ? event.target.closest('.kussin-brand-subcategories')
                    : null;

                if (carousel) {
                    refreshCarousel(carousel);
                }
            }, true);

            document.addEventListener('click', (event) => {
                const control = event.target.closest
                    ? event.target.closest(
                        '[data-kussin-product-carousel-previous], '
                        + '[data-kussin-product-carousel-next], '
                        + '[data-kussin-product-carousel-page]',
                    )
                    : null;
                const carousel = control ? control.closest('[data-kussin-product-carousel]') : null;

                if (!control || !carousel) {
                    return;
                }

                if (control.hasAttribute('data-kussin-product-carousel-previous')) {
                    scrollToPage(carousel, currentPage(carousel) - 1);
                } else if (control.hasAttribute('data-kussin-product-carousel-next')) {
                    scrollToPage(carousel, currentPage(carousel) + 1);
                } else {
                    scrollToPage(carousel, Number(control.dataset.kussinProductCarouselPage));
                }
            });

            document.addEventListener('scroll', (event) => {
                const track = event.target instanceof Element
                    && event.target.matches('[data-kussin-product-carousel-track]')
                    ? event.target
                    : null;
                const carousel = track ? track.closest('[data-kussin-product-carousel]') : null;

                if (!track || !carousel || scheduledTracks.has(track)) {
                    return;
                }

                scheduledTracks.add(track);
                window.requestAnimationFrame(() => {
                    updateCarouselState(carousel);
                    scheduledTracks.delete(track);
                });
            }, true);

            document.addEventListener('touchstart', (event) => {
                const track = event.target.closest
                    ? event.target.closest('[data-kussin-product-carousel-track]')
                    : null;
                const carousel = track ? track.closest('[data-kussin-product-carousel]') : null;
                const touch = event.touches.length === 1 ? event.touches[0] : null;

                if (!track || !carousel || !touch || !carouselIsInfinite(carousel)) {
                    return;
                }

                carouselTouchStarts.set(track, {
                    x: touch.clientX,
                    y: touch.clientY,
                    page: currentPage(carousel),
                });
            }, { passive: true });

            document.addEventListener('touchend', (event) => {
                const track = event.target.closest
                    ? event.target.closest('[data-kussin-product-carousel-track]')
                    : null;
                const carousel = track ? track.closest('[data-kussin-product-carousel]') : null;
                const start = track ? carouselTouchStarts.get(track) : null;
                const touch = event.changedTouches.length === 1 ? event.changedTouches[0] : null;

                if (!track || !carousel || !start || !touch) {
                    return;
                }

                carouselTouchStarts.delete(track);

                const deltaX = touch.clientX - start.x;
                const deltaY = touch.clientY - start.y;
                const lastPage = pageCount(carousel) - 1;

                if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) {
                    return;
                }

                if (start.page === 0 && deltaX > 0) {
                    scrollToPage(carousel, -1);
                } else if (start.page === lastPage && deltaX < 0) {
                    scrollToPage(carousel, lastPage + 1);
                }
            }, { passive: true });

            document.addEventListener('dom-updated', (event) => {
                const carousel = event.target instanceof Element
                    ? event.target.closest('[data-kussin-product-carousel]')
                    : null;

                window.requestAnimationFrame(() => refreshCarousels(carousel || document));
            });

            window.addEventListener('resize', () => {
                if (resizeRequested) {
                    return;
                }

                resizeRequested = true;
                window.requestAnimationFrame(() => {
                    refreshCarousels();
                    resizeRequested = false;
                });
            }, { passive: true });

            if (typeof mobileViewport.addEventListener === 'function') {
                mobileViewport.addEventListener('change', resetCarouselPositions);
            } else {
                mobileViewport.addListener(resetCarouselPositions);
            }
        }

        refreshCarousels();
    }

    window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault();
        installPrompt = event;
        initInstallControl();
    });

    window.addEventListener('appinstalled', () => {
        installPrompt = null;
    });

    document.addEventListener('DOMContentLoaded', () => {
        initInstallControl();
        initFactFinderHeaderSearch();
        initFactFinderMobileAsn();
        initFactFinderListing();
        initFactFinderAsnTooltips();
        initFactFinderSliderLabels();
        initFactFinderRatingSliders();
        initFactFinderFilterCloud();
        initFactFinderCategoryHeader();
        initCmsAccordions();
        initCmsSaleHeadlineLanding();
        initCmsEmptyCategoryDescription();
        initSaleCountdown();
        initCmsNewsletterReveal();
    initSubcatTileFallback();
    initBackToTop();
        initMobileHeader();
    initLiveHeaderWidgets();
        initProductVariantOptions();
        initProductStickyCart();
        initProductInformationPanels();
        initProductGallery();
        initProductGalleryVideos();
        initProductCarousels();
        initEmptyCarouselCollapse();
        initCheckoutFormValidation();
        initContactFormValidation();
    });
    initInstallControl();
    registerServiceWorker();
    initFactFinderHeaderSearch();
    initFactFinderMobileAsn();
    initFactFinderListing();
    initFactFinderAsnTooltips();
    initFactFinderSliderLabels();
    initFactFinderRatingSliders();
    initFactFinderFilterCloud();
    initFactFinderCategoryHeader();
    initBackToTop();
    initMobileHeader();
    initProductVariantOptions();
    initDefectiveCopiesAgreement();
    initFreshBasketToken();
    initProductStickyCart();
    initProductInformationPanels();
    initProductGallery();
    initProductGalleryVideos();
    initProductCarousels();
    initEmptyCarouselCollapse();
    initCheckoutPanels();
    initCheckoutStepSubmit();
    initBasketSelection();
    initCheckoutFormValidation();
    initContactFormValidation();
    initBasketNoticeList();
    window.addEventListener('basketLoaded', initBasketSelection);

    /* APEX has just swapped the <body> for the basket response, placeholders included. Restore the
       header for this visitor and fetch the basket widgets again, since that is what changed. */
    window.addEventListener('basketLoaded', function () {
        initLiveHeaderWidgets('oxwMiniBasket');
    });
})();
